import { useLocation, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect, useRef } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import Loader from "../../../../reusableUI/loader";
import { showToast } from "../../../../reusableUI/toaster";
import Page from "../../../../reusableUI/page";
import { capitalizeFirstLetter } from "../../../../utils/Functions";
import moment from "moment";
import {
  getAssignedStaffList,
  getStandByStaffs,
  assignStaffs,
  actingHCPChange,
  timeSlotChange,
  deleteVisit,
  RejectVisit,
  getphysioSlots,
  getOnetimeSlots,
  getPartner,
} from "../../../../Service/partnerAPI";
import { getLookupData } from "../../../../Service/lookup";
import { CommonImages } from "../../../../reusableUI/images";
import LiveTrackingMap from "../../../../reusableUI/LiveTrackingMap";
import { toast } from "react-toastify";
import PatientHistory from "./patientHistory";
import {
  CalendarCheck2,
  CheckCircle2,
  Clock3,
  RotateCcw,
  X,
} from "lucide-react";
import {
  downloadDetailedBookingsPdf,
  filterExportableBookings,
  getPartnerIdForExport,
} from "../../../../utils/bookingDetailedExport";

const START_DATE_REQUIRED_TOAST_ID = "booking-start-date-required";
const ADVANCE_PAYMENT_MODE_OPTIONS = [
  {
    value: "online",
    label: "Online Payment",
    description: "Patient pays in the normal online flow before duty starts.",
  },
  {
    value: "location_qr",
    label: "On Location QR Payment",
    description: "Patient pays by scanning a QR at the visit location.",
  },
];

const normalizePaymentModeFromVisit = (visit) => {
  const paymentMode = String(visit?.paymentMode || "")
    .trim()
    .toLowerCase();

  if (
    paymentMode === "online" ||
    paymentMode === "location_qr" ||
    paymentMode === "daily_qr"
  ) {
    return paymentMode;
  }

  if (visit?.payOnLocation === true) {
    return "location_qr";
  }

  return "";
};

const isPayOnLocationMode = (paymentMode) => paymentMode === "location_qr";

const getPaymentModeCategory = (paymentMode) => {
  if (paymentMode === "daily_qr") return "daily";
  if (paymentMode === "online" || paymentMode === "location_qr") {
    return "advance";
  }
  return "";
};

const shouldStartWithBlankPaymentModeSelection = (visit) => {
  const visitStatus = String(visit?.status || "")
    .trim()
    .toLowerCase();
  const hasDutyInfo =
    Array.isArray(visit?.dutyInfo) && visit.dutyInfo.length > 0;

  return (
    visit?.bookedBy === "partner" && !hasDutyInfo && visitStatus === "pending"
  );
};

const SortIcon = ({ isSorted, isSortedDesc }) => {
  if (!isSorted) {
    return (
      <svg
        className="h-3 w-3 ml-1 text-gray-400"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 12l5 5 5-5H5zm10-4L10 3 5 8h10z" />
      </svg>
    );
  }

  if (isSortedDesc) {
    return (
      <svg
        className="h-3 w-3 ml-1 text-blue-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 8l5 5 5-5H5z" />
      </svg>
    );
  }

  return (
    <svg
      className="h-3 w-3 ml-1 text-blue-600"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M15 12l-5-5-5 5h10z" />
    </svg>
  );
};

const getDutyStatus = (appointment, currentDate = new Date()) => {
  if (!appointment) return "Unknown";

  const { status, paymentInfo, dutyInfo } = appointment;

  const statusKey = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  // ✅ Early exits — order matters
  if (statusKey === "endduty") return "Duty Ended";
  if (statusKey === "completed") return "Completed";
  if (statusKey === "rejected") return "Cancelled";
  if (statusKey === "cancelled") return "Cancelled"; // ✅ added
  if (statusKey === "expired") return "Expired"; // ✅ moved UP — before paymentInfo block
  if (statusKey === "accepted") return "Confirmed";
  if (statusKey === "pending") return "Unassigned";
  if (statusKey === "notpaid") return "Voided";

  const today = moment(currentDate).format("YYYY-MM-DD");
  const currentDuty = dutyInfo?.find(
    (duty) => moment(duty.date).format("YYYY-MM-DD") === today,
  );

  if (currentDuty) {
    if (currentDuty.endDuty?.verified) return "Duty Ended";
    if (currentDuty.startDuty?.verified) return "Duty Started";
    if (String(currentDuty.liveStatus || "").toLowerCase() === "arrived")
      return "Started";
  }

  if (statusKey === "paymentpending") return "Payment Pending";

  if (
    paymentInfo?.paidInfo?.paid === false &&
    statusKey !== "pending" &&
    statusKey !== "accepted" &&
    statusKey !== "rejected" &&
    statusKey !== "expired"
  ) {
    return "Payment Pending";
  }

  return "Unknown"; // ✅ fallback — never return raw statusKey
};

const isActionRequiredBooking = (appointment) =>
  String(appointment?.status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "") === "pending";

const StatusBadge = ({ status }) => {
  const statusStyles = {
    "Action Required": {
      text: "Action Required",
      textColor: "text-red-600",
    },
    Unassigned: { text: "Unassigned", textColor: "text-red-600" },
    Requested: { text: "Requested", textColor: "text-orange-600" },
    "Payment Pending": {
      text: "Payment Pending",
      textColor: "text-orange-600",
    },
    Voided: { text: "Voided", textColor: "text-red-500" }, // ✅ added
    Confirmed: { text: "Confirmed", textColor: "text-indigo-600" },
    Started: { text: "Started", textColor: "text-blue-600" },
    "Duty Started": { text: "Duty Started", textColor: "text-purple-600" },
    "Duty Ended": { text: "Duty Ended", textColor: "text-gray-600" },
    Completed: { text: "Completed", textColor: "text-teal-600" },
    Cancelled: { text: "Cancelled", textColor: "text-red-700" },
    Expired: { text: "Expired", textColor: "text-gray-500" },
    Unknown: { text: "Unknown", textColor: "text-gray-400" },
  };

  const currentStyle = statusStyles[status] || statusStyles.Unknown;

  return (
    <span
      className={`
        inline-flex items-center justify-center
        px-2.5 py-1
        text-[11px] font-medium font-poppins
        rounded-full
        ${currentStyle.textColor}
        ${currentStyle.bgColor || ""}
        ${currentStyle.borderColor || ""}
      `}
    >
      {currentStyle.text}
    </span>
  );
};

const getServiceDayLabel = (day) => {
  if (day == null) return "";
  const d = Number(day);
  if (Number.isNaN(d)) return "";
  const j = d % 10;
  const k = d % 100;
  let suffix = "th";
  if (j === 1 && k !== 11) suffix = "st";
  else if (j === 2 && k !== 12) suffix = "nd";
  else if (j === 3 && k !== 13) suffix = "rd";
  return `${d}${suffix}`;
};

const getCurrentServiceDayLabel = (visit) => {
  const today = moment().format("YYYY-MM-DD");
  const dutyToday = visit?.dutyInfo?.find((d) => d.date === today);
  if (dutyToday?.day != null) return getServiceDayLabel(dutyToday.day);
  return "-";
};

const isDutyCompleted = (duty) => {
  if (!duty) return false;
  if (
    typeof duty.serviceStatus === "string" &&
    duty.serviceStatus.toLowerCase() === "completed"
  ) {
    return true;
  }
  if (duty.endDuty?.verified) return true;
  return false;
};

const PHYSIO_TIME_SLOTS = [
  // "06:00 AM to 07:00 AM",
  // "07:00 AM to 08:00 AM",
  // "08:00 AM to 09:00 AM",
  // "09:00 AM to 10:00 AM",
  // "10:00 AM to 11:00 AM",
  // "11:00 AM to 12:00 PM",
  // "12:00 PM to 01:00 PM",
  // "01:00 PM to 02:00 PM",
  // "02:00 PM to 03:00 PM",
  // "03:00 PM to 04:00 PM",
  // "04:00 PM to 05:00 PM",
  // "05:00 PM to 06:00 PM",
  // "06:00 PM to 07:00 PM",
  // "07:00 PM to 08:00 PM",
  // "08:00 PM to 09:00 PM",
];

const PHYSIO_MANUAL_ASSIGN_SLOTS = (() => {
  const slots = [];
  for (let hour = 9; hour < 21; hour++) {
    const start = moment({ hour, minute: 0 });
    const end = moment({ hour: hour + 1, minute: 0 });
    slots.push(`${start.format("hh:mm A")} - ${end.format("hh:mm A")}`);
  }
  return slots;
})();

const ONE_TIME_MANUAL_SLOTS = (() => {
  const slots = [];
  for (let hour = 9; hour <= 21; hour++) {
    const start = moment({ hour, minute: 0 });
    slots.push(start.format("hh:mm A"));
  }
  return slots;
})();

const getStaffDisplayName = (staff) => {
  if (!staff) return "";
  const info = staff.personalInfo?.basicInfo?.nameInfo || {};
  const title = info.title || "";
  const fullName = info.fullName || "";
  return title ? `${title}. ${fullName || ""}`.trim() : (fullName || "").trim();
};

const formatAvailabilityRole = (value) =>
  String(value || "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase()) || "-";

const formatAvailabilityDate = (value) => {
  const strictDate = moment(value, "YYYY-MM-DD", true);
  const parsed = strictDate.isValid() ? strictDate : moment(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : "-";
};

const formatAvailabilityTime = (value) => {
  if (!value) return "-";
  const parsed = moment(value).utcOffset("+05:30");
  return parsed.isValid() ? parsed.format("hh:mm A") : "-";
};

const parseSelectedSlot = (slot) => {
  const raw = String(slot || "").trim();
  if (!raw) return { startTimeStr: "", endTimeStr: "" };
  const normalized = raw.includes(" - ") ? raw.replace(" - ", " to ") : raw;
  const [from, to] = normalized.split(/\s+to\s+/i).map((s) => s.trim());
  return {
    startTimeStr: from || "",
    endTimeStr: to || "",
  };
};

const toTimeRangeLabel = (slot) => {
  const { startTimeStr, endTimeStr } = parseSelectedSlot(slot);
  if (startTimeStr && endTimeStr) {
    return `${startTimeStr} to ${endTimeStr}`;
  }
  return String(slot || "").trim();
};

const WEEK_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const normalizeSessionFrequency = (value) => {
  const token = String(value || "")
    .toLowerCase()
    .replace(/\s+/g, "");
  if (!token) return "";
  if (token.includes("alternative")) return "alternative";
  if (token.includes("twice")) return "twice";
  if (token.includes("thrice") || token.includes("three")) return "thrice";
  if (token.includes("daily") || token.includes("everyday")) return "daily";
  return "";
};

const buildSessionDurationDays = ({
  startDate,
  sessionCount,
  frequency,
  excludedDays = [],
  selectedWeekDays = [],
}) => {
  const start = moment(startDate, "YYYY-MM-DD", true);
  if (!start.isValid()) return [];

  const rawSessionCount = Number(sessionCount);
  const safeSessionCount =
    Number.isFinite(rawSessionCount) && rawSessionCount > 0
      ? Math.floor(rawSessionCount)
      : 0;

  if (safeSessionCount <= 0) return [];

  const excludedSet = new Set(
    (Array.isArray(excludedDays) ? excludedDays : []).map((day) =>
      String(day || "").trim(),
    ),
  );

  const weekDaySet = new Set(
    (Array.isArray(selectedWeekDays) ? selectedWeekDays : []).map((day) =>
      String(day || "").trim(),
    ),
  );

  if (excludedSet.size >= 7) return [];
  if (
    (frequency === "twice" || frequency === "thrice") &&
    weekDaySet.size === 0
  )
    return [];

  const result = [];
  let cursor = start.clone();
  let step = 0;
  let safetyCounter = 0;

  while (result.length < safeSessionCount && safetyCounter < 3000) {
    const weekDay = cursor.format("dddd");
    let include = false;

    if (frequency === "alternative") {
      include = step % 2 === 0 && !excludedSet.has(weekDay);
    } else if (frequency === "twice" || frequency === "thrice") {
      include = weekDaySet.has(weekDay);
    } else {
      include = !excludedSet.has(weekDay);
    }

    if (include) {
      result.push(cursor.format("YYYY-MM-DD"));
    }

    cursor.add(1, "day");
    step += 1;
    safetyCounter += 1;
  }

  return result;
};

const CALENDAR_WEEK_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const buildMonthCalendarCells = (monthDate) => {
  const monthStart = monthDate.clone().startOf("month");
  const calendarStart = monthStart.clone().startOf("week");
  const calendarEnd = monthDate.clone().endOf("month").endOf("week");
  const cells = [];
  const cursor = calendarStart.clone();

  while (cursor.isSameOrBefore(calendarEnd, "day")) {
    cells.push({
      dateKey: cursor.format("YYYY-MM-DD"),
      label: cursor.format("D"),
      inCurrentMonth: cursor.month() === monthDate.month(),
    });
    cursor.add(1, "day");
  }

  return cells;
};

const formatLabelToken = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

const normalizeDutyTypeToken = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const parseShiftType = (shiftType = "") => {
  const parts = String(shiftType || "")
    .split(",")
    .map((item) => String(item || "").trim())
    .filter(Boolean);

  return {
    shift: String(parts[0] || "").toLowerCase(),
    hours: String(parts[1] || "").toUpperCase(),
    parts,
  };
};

const toPricingShiftType = (shift = "", hours = "") => {
  const normalizedShift = String(shift || "")
    .toLowerCase()
    .replace(/\s+/g, "");
  const normalizedHours = String(hours || "")
    .toUpperCase()
    .replace(/\s+/g, "");

  if (normalizedShift === "morning") return "Morning";
  if (normalizedShift === "night") return "Night";
  if (
    normalizedShift === "24/7" ||
    normalizedShift === "24x7" ||
    normalizedShift === "247" ||
    normalizedHours === "24H" ||
    normalizedHours === "24"
  ) {
    return "24H";
  }
  return "";
};

const normalizeServiceList = (services = []) => {
  const out = [];
  const seen = new Set();
  for (const item of Array.isArray(services) ? services : []) {
    const value = String(item || "").trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
};

const buildNurseRegularPricingPrefill = (visit) => {
  const roleToken = String(visit?.specialistInfo?.role || "")
    .trim()
    .toLowerCase();
  const role =
    roleToken === "nursing assistant" ||
    roleToken === "nursing-assistant" ||
    roleToken === "nursingassistant"
      ? "nursing-assistant"
      : "nurse";
  const { shift, hours } = parseShiftType(visit?.visitInfo?.shiftType || "");
  const shiftType = toPricingShiftType(shift, hours);
  const duration = String(hours || "").toUpperCase();
  const shiftDuration = shiftType
    ? `${shiftType}|${duration || shiftType}`
    : "";
  const services = normalizeServiceList(visit?.visitInfo?.services);

  return {
    source: "booking-nurse-regular-package",
    lockFields: true,
    visitID: visit?._id || "",
    role,
    shiftType,
    duration,
    shiftDuration,
    services,
  };
};

const buildPhysioAssessmentPricingPrefill = (visit) => ({
  source: "booking-physio-assessment",
  lockFields: true,
  visitID: visit?._id || "",
  role: "physiotherapist",
  service: "Assessment",
});

const buildNurseOneTimePricingNavigationState = (visit) => ({
  source: "booking-nurse-onetime",
  mode: "onetime",
  visitID: visit?._id || "",
  requestedServices: (Array.isArray(visit?.paymentInfo?.serviceFees)
    ? visit.paymentInfo.serviceFees
    : []
  )
    .map((item) => String(item?.service || "").trim())
    .filter(Boolean),
});

const isTwentyFourBySevenShiftType = (shiftType = "") => {
  const { shift, hours } = parseShiftType(shiftType);
  const normalizedShift = String(shift || "")
    .toLowerCase()
    .replace(/\s+/g, "");
  const normalizedHours = String(hours || "")
    .toUpperCase()
    .replace(/\s+/g, "");

  const isTwentyFourBySevenShift =
    normalizedShift === "24/7" ||
    normalizedShift === "24x7" ||
    normalizedShift === "247";
  const isTwentyFourHourDuty =
    normalizedHours === "24" || normalizedHours === "24H";

  return isTwentyFourBySevenShift && isTwentyFourHourDuty;
};

const getRegularLookupTypeByShift = (shiftType = "") => {
  const { shift, hours } = parseShiftType(shiftType);
  if (shift === "morning" && hours === "12H") return "morningSHift12Data";
  if (shift === "morning" && hours === "9H") return "morningShift9Data";
  if (shift === "night" && hours === "12H") return "nightShift12Data";
  return "";
};

const extractLookupSlotValues = (lookupItem) => {
  const options = [];

  if (Array.isArray(lookupItem?.values)) {
    lookupItem.values.forEach((item) => {
      const text = String(
        item?.value ?? item?.label ?? item?.name ?? item?.title ?? item ?? "",
      ).trim();
      if (text) options.push(text);
    });
  }

  const fallback = String(lookupItem?.value ?? lookupItem?.label ?? "").trim();
  if (fallback) options.push(fallback);

  return options;
};

const getDutyTypeDisplay = (visit) => {
  const role = String(visit?.specialistInfo?.role || "")
    .trim()
    .toLowerCase();
  const dutyTypeRaw = String(visit?.dutyType || "").trim();
  const dutyType = normalizeDutyTypeToken(dutyTypeRaw);

  if (role === "physiotherapist") return "Session";

  if (
    role === "nurse" ||
    role === "nursing-assistant" ||
    role === "nursing assistant" ||
    role === "nursingassistant"
  ) {
    if (dutyType === "onetime") return "One Time";

    if (dutyType === "regular") {
      const { shift, hours } = parseShiftType(
        visit?.visitInfo?.shiftType || "",
      );
      if (shift && hours) return `${formatLabelToken(shift)}, ${hours}`;
      if (shift) return formatLabelToken(shift);
      return "Regular";
    }
  }

  if (dutyType === "onetime") return "One Time";

  const shiftTypeRaw = String(visit?.visitInfo?.shiftType || "").trim();
  if (shiftTypeRaw && dutyTypeRaw) return `${shiftTypeRaw}, ${dutyTypeRaw}`;
  if (shiftTypeRaw) return shiftTypeRaw;
  if (dutyTypeRaw) return dutyTypeRaw;
  return "-";
};

const getLocationDisplay = (visit) => {
  const cityRaw = String(visit?.visitInfo?.addressInfo?.city || "").trim();
  const districtRaw = String(
    visit?.visitInfo?.addressInfo?.district || "",
  ).trim();

  const city = cityRaw ? formatLabelToken(cityRaw) : "";
  const district = districtRaw ? formatLabelToken(districtRaw) : "";

  if (city && district) return `${city}, ${district}`;
  if (city) return city;
  if (district) return district;
  return "-";
};

const computeEndDateFromStartAndDays = (startDate, days) => {
  const parsed = moment(startDate, "YYYY-MM-DD", true);
  if (!parsed.isValid()) return "";
  const dayCount = Number(days);
  const safeDays = Number.isFinite(dayCount) && dayCount > 0 ? dayCount : 1;
  return parsed.add(safeDays - 1, "day").format("YYYY-MM-DD");
};

const PatientList = ({ searchTerm, bookingsData, from }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // readOnly = admin is viewing via bookingsData prop; partner has full access
  const readOnly = Boolean(bookingsData) && from === "Requests";
  const canEdit = !readOnly; // ✅ single source of truth for edit permissions

  const [loading, setLoading] = useState(true);
  const [visitList, setVisitList] = useState([]);
  const [paginationPage, setPaginationPage] = useState(0);
  const [selectedPageCount, setSelectedPageCount] = useState(() => {
    const saved = localStorage.getItem("bookingsPageCount");
    return saved ? Number(saved) : 50;
  });

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [assignStaffList, setAssignStaffList] = useState([]);
  const [availabilityStaffList, setAvailabilityStaffList] = useState([]);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [availabilityContext, setAvailabilityContext] = useState(null);
  const availabilityRequestIdRef = useRef(0);
  const [assessmentStaff, setAssessmentStaff] = useState("");
  const [selectedMainStaff, setSelectedMainStaff] = useState(null);
  const [selectedStandby1, setSelectedStandby1] = useState(null);
  const [selectedStandby2, setSelectedStandby2] = useState(null);
  const [isEditingStaff, setIsEditingStaff] = useState(false);
  const [editingDutyIndex, setEditingDutyIndex] = useState(null);
  const [changingDutyStaff, setChangingDutyStaff] = useState({});
  const [showTracking, setShowTracking] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [dutySlots, setDutySlots] = useState({});
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [timelineVisit, setTimelineVisit] = useState(null);
  const [timeModalDutyIndex, setTimeModalDutyIndex] = useState(null);
  const [timeModalDutyKey, setTimeModalDutyKey] = useState(null);
  const [timeModalSelectedSlot, setTimeModalSelectedSlot] = useState("");
  const [manualStartDate, setManualStartDate] = useState("");
  const [manualEndDate, setManualEndDate] = useState("");
  const [manualSlot, setManualSlot] = useState("");
  const [manualExcludeSaturday, setManualExcludeSaturday] = useState(false);
  const [manualExcludeSunday, setManualExcludeSunday] = useState(false);
  const [manualSelectedWeekDays, setManualSelectedWeekDays] = useState([]);
  const [showCalendarPreview, setShowCalendarPreview] = useState(false);
  const [calendarPreviewMonthKey, setCalendarPreviewMonthKey] = useState("");
  const [lookupData, setLookupData] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [dynamicSlots, setDynamicSlots] = useState([]);
  const [dynamicSlotsLoading, setDynamicSlotsLoading] = useState(false);
  const [dynamicSlotsError, setDynamicSlotsError] = useState("");
  const [requestFilter, setRequestFilter] = useState("All");
  const [paymentMode, setPaymentMode] = useState("");
  const [advancePaymentMode, setAdvancePaymentMode] = useState("");
  const [paymentModeCategory, setPaymentModeCategory] = useState("");
  const [exportingPdf, setExportingPdf] = useState(false);

  const requestFilterOptions = [
    { label: "All", value: "All" },
    { label: "Payment Pending", value: "pending" },
    { label: "Confirmed", value: "confirmed" },
    { label: "Completed", value: "completed" },
    { label: "Others", value: "others" },
  ];
  useEffect(() => {
    // ✅ In readOnly mode, use passed bookingsData — no API call
    if (readOnly) {
      setVisitList(Array.isArray(bookingsData) ? bookingsData : []);
      setLoading(false);
      return;
    }
    getVisit();
  }, [readOnly, bookingsData, from]);

  useEffect(() => {
    if (!location?.state?.fromPricingPrefill) return;
    if (!readOnly) getVisit();
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    location?.state?.fromPricingPrefill,
    location.pathname,
    navigate,
    readOnly,
  ]);

  const getVisit = async () => {
    setLoading(true);
    try {
      const result = await getAssignedStaffList();
      setVisitList(result?.data || []);
    } catch (error) {
      console.error("Error fetching assignment data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visitList.length > 0 && !selectedPatient) {
      const firstPatient =
        visitList.find(isActionRequiredBooking) || visitList[0];

      if (firstPatient.dutyInfo && firstPatient.dutyInfo.length > 0) {
        const updatedDutyInfo = firstPatient.dutyInfo.map((duty) => {
          if (duty.attendedBy && duty.attendedBy.userID) return duty;

          const assignedStaffId = firstPatient.specialistInfo?.userID?.$oid;
          const assignedStaffName = firstPatient.specialistInfo?.name;
          const assignedStaffRole =
            firstPatient.specialistInfo?.role?.toLowerCase() || "nurse";

          return {
            ...duty,
            attendedBy: {
              userID: { $oid: assignedStaffId },
              role: assignedStaffRole,
            },
            assignedStaffName,
          };
        });

        setSelectedPatient({
          ...firstPatient,
          dutyInfo: updatedDutyInfo,
        });
      } else {
        setSelectedPatient(firstPatient);
      }
    }
  }, [visitList, selectedPatient]);

  useEffect(() => {
    if (!selectedPatient) {
      setManualStartDate("");
      setManualEndDate("");
      setManualSlot("");
      setManualExcludeSaturday(false);
      setManualExcludeSunday(false);
      setManualSelectedWeekDays([]);
      setShowCalendarPreview(false);
      setLookupError("");
      setDynamicSlots([]);
      setPaymentMode("");
      setAdvancePaymentMode("");
      setPaymentModeCategory("");
      return;
    }

    const startDate = selectedPatient?.visitInfo?.startDate
      ? moment(selectedPatient.visitInfo.startDate).format("YYYY-MM-DD")
      : "";
    const endDate = selectedPatient?.visitInfo?.endDate
      ? moment(selectedPatient.visitInfo.endDate).format("YYYY-MM-DD")
      : "";

    setManualStartDate(startDate);
    setManualEndDate(endDate);
    setManualSlot(selectedPatient?.visitInfo?.slot || "");

    const activeAssessment = Array.isArray(selectedPatient?.assessment)
      ? selectedPatient.assessment.find((item) => item?.active)
      : null;

    const existingExcludedDays = Array.isArray(
      activeAssessment?.treatmentPlan?.excludedDays,
    )
      ? activeAssessment.treatmentPlan.excludedDays
      : [];

    setManualExcludeSaturday(existingExcludedDays.includes("Saturday"));
    setManualExcludeSunday(existingExcludedDays.includes("Sunday"));

    const existingSessionDays = Array.isArray(
      activeAssessment?.treatmentPlan?.sessionDurationDays,
    )
      ? activeAssessment.treatmentPlan.sessionDurationDays
      : [];

    const existingWeekDays = [
      ...new Set(
        existingSessionDays
          .map((item) => moment(item, "YYYY-MM-DD", true))
          .filter((item) => item.isValid())
          .map((item) => item.format("dddd")),
      ),
    ];

    setManualSelectedWeekDays(existingWeekDays);
    setShowCalendarPreview(false);
    setLookupError("");
    if (shouldStartWithBlankPaymentModeSelection(selectedPatient)) {
      setPaymentMode("");
      setAdvancePaymentMode("");
      setPaymentModeCategory("");
      return;
    }

    const normalizedPaymentMode =
      normalizePaymentModeFromVisit(selectedPatient);
    setPaymentMode(normalizedPaymentMode);
    setAdvancePaymentMode(
      normalizedPaymentMode === "online" ||
        normalizedPaymentMode === "location_qr"
        ? normalizedPaymentMode
        : "",
    );
    setPaymentModeCategory(getPaymentModeCategory(normalizedPaymentMode));
  }, [selectedPatient?._id]);

  const getNames = async (includeAvailability = false) => {
    if (!selectedPatient || readOnly) return; // ✅ block in readOnly

    const staff = selectedPatient?.specialistInfo?.role || null;
    const subRole = selectedPatient?.specialistInfo?.subRole
      ? selectedPatient?.specialistInfo?.subRole
      : staff === "physiotherapist"
        ? "physiotherapist"
        : staff === "nurse"
          ? "nurse"
          : null;

    const visitStartDate = selectedPatient?.visitInfo?.startDate
      ? moment(selectedPatient.visitInfo.startDate).format("YYYY-MM-DD")
      : "";
    const selectedStartDate = manualStartDate || visitStartDate;
    const visitEndDate = selectedPatient?.visitInfo?.endDate
      ? moment(selectedPatient.visitInfo.endDate).format("YYYY-MM-DD")
      : selectedStartDate;
    const selectedEndDate = effectiveManualEndDate || visitEndDate;
    const selectedSlot = effectiveManualSlot;

    if (!selectedStartDate) {
      if (includeAvailability) {
        setAvailabilityError(
          "Select the service start date before checking availability.",
        );
      } else if (needsManualVisitInfo) {
        if (!toast.isActive(START_DATE_REQUIRED_TOAST_ID)) {
          toast.warning("Please select start date before selecting staff", {
            toastId: START_DATE_REQUIRED_TOAST_ID,
          });
        }
      }
      return;
    }

    if (!selectedSlot && !includeAvailability) {
      toast.warning("Please select slot before selecting staff");
      return;
    }

    const isDateOnlyAvailability = includeAvailability && !selectedSlot;
    const sessionDates = isDateOnlyAvailability
      ? []
      : effectiveAvailabilitySessionDates;
    const availabilityEndDate = isDateOnlyAvailability
      ? selectedStartDate
      : selectedEndDate || selectedStartDate;

    const requestId = availabilityRequestIdRef.current + 1;
    availabilityRequestIdRef.current = requestId;
    if (includeAvailability) {
      setAvailabilityLoading(true);
      setAvailabilityError("");
      setAvailabilityStaffList([]);
      setAvailabilityContext({
        role: staff,
        subRole,
        startDate: selectedStartDate,
        endDate: availabilityEndDate,
        slot: selectedSlot,
        sessionDates,
      });
    }

    try {
      const result = await getStandByStaffs(staff, selectedStartDate, subRole, {
        visitID: selectedPatient?._id,
        dutyType: selectedPatient?.dutyType,
        endDate: availabilityEndDate,
        slot: selectedSlot,
        shiftType: selectedPatient?.visitInfo?.shiftType,
        sessionDates,
        ...(includeAvailability ? { includeAvailability: true } : {}),
      });

      if (availabilityRequestIdRef.current !== requestId) {
        return;
      }

      if (!result || result.error || !Array.isArray(result.data)) {
        if (includeAvailability) {
          setAvailabilityError(
            result?.message || "Unable to load staff availability.",
          );
        }
        return;
      }

      if (includeAvailability) {
        const allSpecialists = result.data;
        setAvailabilityStaffList(allSpecialists);
        if (selectedSlot) {
          setAssignStaffList(
            allSpecialists.filter(
              (specialist) => specialist?.availability?.status !== "occupied",
            ),
          );
        } else {
          setAssignStaffList([]);
        }
      } else {
        setAssignStaffList(result.data);
      }
    } finally {
      if (
        includeAvailability &&
        availabilityRequestIdRef.current === requestId
      ) {
        setAvailabilityLoading(false);
      }
    }
  };

  const openAvailabilityModal = () => {
    if (!hasAvailabilityDate) return;
    setAvailabilityModalOpen(true);
    setAvailabilityError("");
    getNames(true);
  };

  const closeAvailabilityModal = () => {
    setAvailabilityModalOpen(false);
  };

  const getStandbyStaffList = () => {
    const standbyList = [];
    const sps = selectedPatient?.standBySpecialists;

    if (sps?.specialists1UserID) {
      standbyList.push({
        id: String(sps.specialists1UserID),
        name: sps.specialists1Name,
      });
    }

    if (sps?.specialists2UserID) {
      standbyList.push({
        id: String(sps.specialists2UserID),
        name: sps.specialists2Name,
      });
    }

    if (selectedPatient?.standBySpecialist?.userID) {
      const id = String(selectedPatient.standBySpecialist.userID);
      if (!standbyList.some((s) => s.id === id)) {
        standbyList.push({
          id,
          name: selectedPatient.standBySpecialist.name,
        });
      }
    }

    if (selectedPatient?.backupSpecialist?.userID) {
      const id = String(selectedPatient.backupSpecialist.userID);
      if (!standbyList.some((s) => s.id === id)) {
        standbyList.push({
          id,
          name: selectedPatient.backupSpecialist.name,
        });
      }
    }

    return standbyList;
  };

  const resetAssignState = () => {
    setSelectedMainStaff(null);
    setSelectedStandby1(null);
    setSelectedStandby2(null);
    setAssessmentStaff("");
    setIsEditingStaff(false);
    setEditingDutyIndex(null);
    setChangingDutyStaff({});
    setDutySlots({});
    setTimeModalOpen(false);
    setTimeModalDutyIndex(null);
    setTimeModalDutyKey(null);
    setTimeModalSelectedSlot("");
    setManualStartDate("");
    setManualEndDate("");
    setManualSlot("");
    setManualExcludeSaturday(false);
    setManualExcludeSunday(false);
    setManualSelectedWeekDays([]);
    setShowCalendarPreview(false);
    setLookupError("");
    setPaymentMode("");
    setAdvancePaymentMode("");
    setPaymentModeCategory("");
  };

  const handleRowClick = (patient) => {
    resetAssignState();

    if (selectedPatient?._id === patient._id) {
      setSelectedPatient(patient);
    } else {
      if (patient.dutyInfo && patient.dutyInfo.length > 0) {
        const updatedDutyInfo = patient.dutyInfo.map((duty) => {
          if (duty.attendedBy && duty.attendedBy.userID) return duty;

          const assignedStaffId = patient.specialistInfo?.userID?.$oid;
          const assignedStaffName = patient.specialistInfo?.name;
          const assignedStaffRole =
            patient.specialistInfo?.role?.toLowerCase() || "nurse";

          return {
            ...duty,
            attendedBy: {
              userID: { $oid: assignedStaffId },
              role: assignedStaffRole,
            },
            assignedStaffName,
          };
        });

        setSelectedPatient({
          ...patient,
          dutyInfo: updatedDutyInfo,
        });
      } else {
        setSelectedPatient(patient);
      }
    }
  };

  const handleSingleStaffChange = (staffId) => {
    if (readOnly) return; // ✅ block in readOnly
    setSelectedMainStaff(staffId);
    setIsEditingStaff(true);
    if (selectedStandby1 === staffId) setSelectedStandby1(null);
    if (selectedStandby2 === staffId) setSelectedStandby2(null);
  };

  const handleManualWeekDayToggle = (day) => {
    if (readOnly) return; // ✅ block in readOnly

    const dayName = String(day || "").trim();
    if (!dayName) return;

    setManualSelectedWeekDays((prev) => {
      const exists = prev.includes(dayName);
      if (exists) return prev.filter((item) => item !== dayName);

      if (requiredWeekDayCount > 0 && prev.length >= requiredWeekDayCount) {
        return prev;
      }
      return [...prev, dayName];
    });

    setIsEditingStaff(true);
  };

  const handleDutyStaffChange = async (dutyIndex, staffId) => {
    if (readOnly) return; // ✅ block in readOnly
    if (!staffId || !selectedPatient) return;

    const duty = selectedPatient.dutyInfo[dutyIndex];

    if (isDutyCompleted(duty)) {
      alert("Cannot change staff for a completed service.");
      return;
    }

    const selectedStaff = getStandbyStaffList().find(
      (staff) => staff.id === staffId,
    );

    if (!selectedStaff) {
      alert("Selected staff not found");
      return;
    }

    const currentStaffName =
      duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo?.title &&
      duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo?.fullName
        ? `${duty.attendedBy.userID.personalInfo.basicInfo.nameInfo.title} ${duty.attendedBy.userID.personalInfo.basicInfo.nameInfo.fullName}`
        : "N/A";

    const confirmChange = window.confirm(
      `Are you sure you want to change staff for ${moment(duty.date).format(
        "DD/MM/YYYY",
      )}?\n\nFrom: ${currentStaffName}\nTo: ${selectedStaff.name}`,
    );

    if (!confirmChange) {
      setEditingDutyIndex(null);
      const updatedChangingStaff = { ...changingDutyStaff };
      delete updatedChangingStaff[dutyIndex];
      setChangingDutyStaff(updatedChangingStaff);
      return;
    }

    const currentPatientId = selectedPatient?._id;

    const data = {
      visitID: currentPatientId,
      specialistID: selectedStaff.id,
      date: moment(duty.date).format("YYYY-MM-DD"),
    };

    try {
      await actingHCPChange(data);
      await getVisit();

      alert("Staff changed successfully!");
      setEditingDutyIndex(null);
      const updatedChangingStaff = { ...changingDutyStaff };
      delete updatedChangingStaff[dutyIndex];
      setChangingDutyStaff(updatedChangingStaff);
    } catch (error) {
      console.error("Error changing staff:", error);
      alert("Failed to change staff. Please try again.");

      setEditingDutyIndex(null);
      const updatedChangingStaff = { ...changingDutyStaff };
      delete updatedChangingStaff[dutyIndex];
      setChangingDutyStaff(updatedChangingStaff);
    }
  };

  const handleCloseDropdown = (dutyIndex) => {
    setEditingDutyIndex(null);
    const updatedChangingStaff = { ...changingDutyStaff };
    delete updatedChangingStaff[dutyIndex];
    setChangingDutyStaff(updatedChangingStaff);
  };

  const handleSaveStaffAssignment = async () => {
    if (readOnly) return; // ✅ block in readOnly

    if (!selectedPatient) {
      showToast("No patient selected", "Error");
      return;
    }

    if (!selectedMainStaff || !selectedStandby1) {
      showToast("Please select main staff and standby staff 1", "Error");
      return;
    }

    if (needsManualVisitInfo) {
      if (!effectiveManualStartDate) {
        showToast("Please select start date", "Error");
        return;
      }

      if (!effectiveManualEndDate) {
        showToast("Unable to calculate end date", "Error");
        return;
      }

      if (!effectiveManualSlot) {
        showToast("Please select slot", "Error");
        return;
      }

      if (
        isPhysiotherapist &&
        isTwiceOrThriceFrequency &&
        manualSelectedWeekDays.length !== requiredWeekDayCount
      ) {
        showToast(
          `Please select exactly ${requiredWeekDayCount} day${
            requiredWeekDayCount > 1 ? "s" : ""
          } for ${activeSessionFrequency || "selected frequency"}`,
          "Error",
        );
        return;
      }

      if (
        isPhysiotherapist &&
        computedPhysioSessionDurationDays.length !== effectiveSessionCount
      ) {
        showToast(
          `Unable to build ${effectiveSessionCount} sessions with selected frequency and days`,
          "Error",
        );
        return;
      }
    }

    const safeStaffList = Array.isArray(assignStaffList) ? assignStaffList : [];

    const originalStaff = safeStaffList.find(
      (item) => item && item._id === selectedMainStaff,
    );

    const standby1Staff = safeStaffList.find(
      (item) => item && item._id === selectedStandby1,
    );

    const standby2Staff = selectedStandby2
      ? safeStaffList.find((item) => item && item._id === selectedStandby2)
      : null;

    const effectiveAssessmentStaffId =
      isPhysiotherapist && !assessmentStaff
        ? selectedMainStaff
        : assessmentStaff;

    const assessmentStaffDoc = effectiveAssessmentStaffId
      ? safeStaffList.find(
          (item) => item && item._id === effectiveAssessmentStaffId,
        )
      : null;

    if (!originalStaff) {
      showToast("Selected main staff not found", "Error");
      return;
    }

    if (!standby1Staff) {
      showToast("Selected standby staff 1 not found", "Error");
      return;
    }

    if (!paymentMode) {
      showToast("Please select payment mode", "Error");
      return;
    }

    const currentPatientId = selectedPatient._id;

    const payload = {
      visitID: currentPatientId,
      paymentMode,
      payOnLocation: isPayOnLocationMode(paymentMode),
      dutyStaff: {
        userID: originalStaff._id,
        name: getStaffDisplayName(originalStaff),
      },
      standBySpecialist: {
        userID: standby1Staff._id,
        name: getStaffDisplayName(standby1Staff),
      },
    };

    const isPhysioNoAssessment =
      selectedPatient?.specialistInfo?.role === "physiotherapist" &&
      selectedPatient?.isAssessment === false;

    if (!isPhysioNoAssessment) {
      payload.assessmentStaff = assessmentStaffDoc
        ? {
            userID: assessmentStaffDoc._id,
            name: getStaffDisplayName(assessmentStaffDoc),
          }
        : null;
    }

    if ((isPhysioNoAssessment || !isPhysiotherapist) && standby2Staff) {
      payload.backupSpecialist = {
        userID: standby2Staff._id,
        name: getStaffDisplayName(standby2Staff),
      };
    }

    if (needsManualVisitInfo) {
      const normalizedSlot = toTimeRangeLabel(effectiveManualSlot);
      payload.startDate = moment(effectiveManualStartDate, "YYYY-MM-DD", true)
        .utc()
        .format();
      payload.endDate = moment(effectiveManualEndDate, "YYYY-MM-DD", true)
        .utc()
        .format();
      payload.slot = normalizedSlot;

      if (isPhysiotherapist) {
        const sessionDurationDays = [...computedPhysioSessionDurationDays];
        const sessionStartDate =
          sessionDurationDays[0] || effectiveManualStartDate;
        const sessionEndDate =
          sessionDurationDays[sessionDurationDays.length - 1] ||
          effectiveManualEndDate;
        const requestedDate = moment().format("YYYY-MM-DD");
        const { startTimeStr, endTimeStr } = parseSelectedSlot(normalizedSlot);

        const sessionStartMoment = moment(
          `${sessionStartDate} ${startTimeStr}`,
          "YYYY-MM-DD hh:mm A",
          true,
        );

        const sessionEndMoment = moment(
          `${sessionStartDate} ${endTimeStr}`,
          "YYYY-MM-DD hh:mm A",
          true,
        );

        payload.treatmentPlan = {
          status: "pending",
          requestedDate,
          sessionDurationDays,
          sessionStartDate,
          sessionEndDate,
          excludedDays: physioExcludedDays,
          ...(sessionStartMoment.isValid()
            ? { sessionStartTime: sessionStartMoment.utc().format() }
            : {}),
          ...(sessionEndMoment.isValid()
            ? { sessionEndTime: sessionEndMoment.utc().format() }
            : {}),
          ...(startTimeStr && endTimeStr
            ? {
                timings: {
                  startTime: sessionStartMoment.isValid()
                    ? sessionStartMoment.format()
                    : "",
                  endTime: sessionEndMoment.isValid()
                    ? sessionEndMoment.format()
                    : "",
                  label: normalizedSlot,
                },
              }
            : {}),
        };
      }
    }

    setLoading(true);

    try {
      // console.log(payload);
      const res = await assignStaffs(payload);
      // const res = [];

      if (res?.error) {
        setLoading(false);
        showToast(res?.message || "Failed to assign staff", "Error");
        setSelectedMainStaff(null);
        setSelectedStandby1(null);
        setSelectedStandby2(null);
        setAssessmentStaff("");
        setIsEditingStaff(false);
        return;
      }

      const result = await getAssignedStaffList();

      if (result?.data) {
        const list = result.data || [];
        setVisitList(list);

        const refreshedPatient =
          currentPatientId && list.find((p) => p && p._id === currentPatientId);

        if (refreshedPatient) setSelectedPatient(refreshedPatient);
        else setSelectedPatient(null);
      }

      setSelectedMainStaff(null);
      setSelectedStandby1(null);
      setSelectedStandby2(null);
      setAssessmentStaff("");
      setIsEditingStaff(false);
      setPaymentMode("");
      setAdvancePaymentMode("");
      setPaymentModeCategory("");

      setLoading(false);
      showToast("Staff assigned successfully!", "Success");
    } catch (error) {
      setLoading(false);
      console.error("Error assigning staff:", error);
      showToast("Failed to assign staff", "Error");
    }
  };

  const handleCancelStaffAssignment = () => {
    setSelectedMainStaff(null);
    setSelectedStandby1(null);
    setSelectedStandby2(null);
    setAssessmentStaff("");
    setIsEditingStaff(false);

    setManualStartDate(
      selectedPatient?.visitInfo?.startDate
        ? moment(selectedPatient.visitInfo.startDate).format("YYYY-MM-DD")
        : "",
    );

    setManualEndDate(
      selectedPatient?.visitInfo?.endDate
        ? moment(selectedPatient.visitInfo.endDate).format("YYYY-MM-DD")
        : "",
    );

    setManualSlot(selectedPatient?.visitInfo?.slot || "");

    const activeAssessment = Array.isArray(selectedPatient?.assessment)
      ? selectedPatient.assessment.find((item) => item?.active)
      : null;

    const existingExcludedDays = Array.isArray(
      activeAssessment?.treatmentPlan?.excludedDays,
    )
      ? activeAssessment.treatmentPlan.excludedDays
      : [];

    setManualExcludeSaturday(existingExcludedDays.includes("Saturday"));
    setManualExcludeSunday(existingExcludedDays.includes("Sunday"));

    const existingSessionDays = Array.isArray(
      activeAssessment?.treatmentPlan?.sessionDurationDays,
    )
      ? activeAssessment.treatmentPlan.sessionDurationDays
      : [];

    const existingWeekDays = [
      ...new Set(
        existingSessionDays
          .map((item) => moment(item, "YYYY-MM-DD", true))
          .filter((item) => item.isValid())
          .map((item) => item.format("dddd")),
      ),
    ];

    setManualSelectedWeekDays(existingWeekDays);
    setShowCalendarPreview(false);
    setLookupError("");
    if (shouldStartWithBlankPaymentModeSelection(selectedPatient)) {
      setPaymentMode("");
      setAdvancePaymentMode("");
      setPaymentModeCategory("");
      return;
    }

    const normalizedPaymentMode =
      normalizePaymentModeFromVisit(selectedPatient);
    setPaymentMode(normalizedPaymentMode);
    setAdvancePaymentMode(
      normalizedPaymentMode === "online" ||
        normalizedPaymentMode === "location_qr"
        ? normalizedPaymentMode
        : "",
    );
    setPaymentModeCategory(getPaymentModeCategory(normalizedPaymentMode));
  };

  const handleDeleteVisit = async (visitId) => {
    if (readOnly) return; // ✅ block in readOnly

    const confirmDelete = window.confirm(
      "Are you sure want to cancel this booking?",
    );
    if (!confirmDelete) return;

    try {
      const res = await deleteVisit({ visitID: visitId });
      if (res?.error === false) {
        toast.success("Booking cancelled successfully");
        if (selectedPatient?._id === visitId) {
          setSelectedPatient(null);
        }
        await getVisit();
      } else {
        toast.error(res?.message || "Failed to cancel booking");
      }
    } catch (error) {
      console.error("Error deleting visit:", error);
      toast.error("Failed to cancel booking");
    }
  };

  const openTimelineModal = (visit) => {
    setTimelineVisit(visit);
    setTimelineModalOpen(true);
  };

  const closeTimelineModal = () => {
    setTimelineModalOpen(false);
    setTimelineVisit(null);
  };

  const handleRejectPricingRequest = async () => {
    const visitId = selectedPatient?._id;
    if (!visitId) return;

    const confirmReject = window.confirm(
      "Are you sure you want to reject this pricing request?",
    );
    if (!confirmReject) return;

    try {
      const res = await RejectVisit(visitId);
      if (res?.error === false) {
        toast.success("Request rejected successfully");
        if (selectedPatient?._id === visitId) {
          setSelectedPatient(null);
        }
        await getVisit();
      } else {
        toast.error(res?.message || "Failed to reject request");
      }
    } catch (error) {
      console.error("Error rejecting pricing request:", error);
      toast.error("Failed to reject request");
    }
  };

  const handleConfigurePricing = () => {
    if (!selectedPatient) return;
    const role = String(selectedPatient?.specialistInfo?.role || "")
      .trim()
      .toLowerCase();
    const dutyType = String(selectedPatient?.dutyType || "")
      .trim()
      .toLowerCase();
    const prefill =
      role === "physiotherapist"
        ? buildPhysioAssessmentPricingPrefill(selectedPatient)
        : role === "nurse" && dutyType === "onetime"
          ? buildNurseOneTimePricingNavigationState(selectedPatient)
          : buildNurseRegularPricingPrefill(selectedPatient);
    // console.log("[Booking] configure pricing payload:", prefill);
    navigate("/partner/pricing", {
      state: {
        refreshKey: Date.now(),
        prefill,
        returnTo: location.pathname,
      },
    });
  };

  const calculateAge = (dob) => {
    if (!dob) return "-";
    const age = moment().diff(moment(dob), "years");
    return `${age} yrs`;
  };

  const isPendingStatus = (patient) =>
    patient?.status?.toLowerCase() === "pending";

  const isRequestedStatus = (patient) =>
    patient?.status?.toLowerCase() === "payment pending";

  const normalizeStatus = (v) =>
    String(v || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");

  const getStatusKey = (item) => normalizeStatus(item?.status);

  const isPendingBucket = (item) => {
    const s = getStatusKey(item);
    return s === "pending" || s === "paymentpending";
  };
  const isConfirmedBucket = (item) => {
    const s = getStatusKey(item);
    return s === "accepted" || s === "startduty" || s === "endduty";
  };

  const isCompletedBucket = (item) => {
    const s = getStatusKey(item);
    return s === "completed";
  };

  const isOthersBucket = (item) => {
    const s = getStatusKey(item);
    return (
      s === "expired" ||
      s === "rejected" ||
      s === "skipped" ||
      s === "notpaid" ||
      s === "cancelled"
    );
  };

  const bucketPredicate = (bucket) => {
    switch (bucket) {
      case "pending":
        return isPendingBucket;
      case "confirmed":
        return isConfirmedBucket;
      case "completed":
        return isCompletedBucket;
      case "others":
        return isOthersBucket;
      default:
        return () => true;
    }
  };

  const getAssignedHCPName = (visit) => {
    if (!visit) return "N/A";
    return visit?.dutyStaff?.name || visit?.specialistInfo?.name || "N/A";
  };

  const getAssessmentHCPName = (visit) => {
    if (!visit) return "N/A";
    return visit?.assessmentStaff?.name || visit?.specialistInfo?.name || "N/A";
  };

  const getStandbyHCPName = (visit) => {
    if (!visit) return "N/A";
    return (
      visit?.standBySpecialist?.name ||
      visit?.standBySpecialists?.specialists1Name ||
      "N/A"
    );
  };

  const getServiceDateDisplay = (visitInfo) => {
    if (!visitInfo) return "-";
    const { startDate, endDate } = visitInfo;

    if (startDate && endDate) {
      const startStr = moment(startDate).format("DD/MM/YYYY");
      const endStr = moment(endDate).format("DD/MM/YYYY");
      if (startStr === endStr) return startStr;
      return `${startStr} - ${endStr}`;
    }

    if (startDate) return moment(startDate).format("DD/MM/YYYY");
    if (endDate) return moment(endDate).format("DD/MM/YYYY");
    return "-";
  };

  const filteredData = useMemo(() => {
    let filtered = Array.isArray(visitList) ? [...visitList] : [];

    if (requestFilter === "All") {
      filtered = filtered.filter((item) => {
        const s = getStatusKey(item);
        return (
          s !== "notpaid" &&
          s !== "voided" &&
          s !== "cancelled" &&
          s !== "expired" &&
          s !== "rejected"
        );
      });
    } else {
      filtered = filtered.filter(bucketPredicate(requestFilter));
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        const name =
          item?.patient?.name?.toLowerCase() ||
          item?.patientInfo?.personalInfo?.basicInfo?.nameInfo?.fullName?.toLowerCase() ||
          "";
        const phone = String(
          item?.patient?.phone ?? item?.patientInfo?.phone ?? "",
        );
        return name.includes(term) || phone.includes(term);
      });
    }

    return filtered;
  }, [visitList, searchTerm, requestFilter]);

  const handleExportDetailedPdf = async () => {
    if (exportingPdf) return;

    const exportSource =
      Array.isArray(filteredData) && filteredData.length > 0
        ? filteredData
        : visitList;
    const exportable = filterExportableBookings(exportSource);

    if (!exportable.length) {
      showToast(
        "No accepted, startDuty, endDuty, or completed bookings found for export.",
        "Error",
      );
      return;
    }

    setExportingPdf(true);
    try {
      const partnerResponse = await getPartner(
        getPartnerIdForExport(exportable[0]),
      );
      const result = await downloadDetailedBookingsPdf({
        visits: exportable,
        partner: partnerResponse?.data || null,
        sourceLabel: "Partner Bookings",
      });
      showToast(
        `Exported ${result.count} booking(s) to ${result.fileName}`,
        "Success",
      );
    } catch (error) {
      console.error("Booking detailed PDF export failed:", error);
      showToast(
        error?.message || "Failed to export detailed booking PDF.",
        "Error",
      );
    } finally {
      setExportingPdf(false);
    }
  };

  const orderedData = useMemo(() => {
    const actionRequired = [];
    const remaining = [];

    filteredData.forEach((item) => {
      if (isActionRequiredBooking(item)) {
        actionRequired.push(item);
      } else {
        remaining.push(item);
      }
    });

    return [...actionRequired, ...remaining];
  }, [filteredData]);

  useEffect(() => {
    if (!orderedData.length) {
      setSelectedPatient(null);
      setDutySlots({});
      setTimeModalOpen(false);
      setTimeModalDutyIndex(null);
      setTimeModalDutyKey(null);
      setTimeModalSelectedSlot("");
      return;
    }

    if (
      selectedPatient &&
      orderedData.some((p) => p && p._id === selectedPatient._id)
    ) {
      return;
    }

    const first = orderedData[0];
    setDutySlots({});
    setTimeModalOpen(false);
    setTimeModalDutyIndex(null);
    setTimeModalDutyKey(null);
    setTimeModalSelectedSlot("");

    if (first.dutyInfo && first.dutyInfo.length > 0) {
      const updatedDutyInfo = first.dutyInfo.map((duty) => {
        if (duty.attendedBy && duty.attendedBy.userID) return duty;

        const assignedStaffId = first.specialistInfo?.userID?.$oid;
        const assignedStaffName = first.specialistInfo?.name;
        const assignedStaffRole =
          first.specialistInfo?.role?.toLowerCase() || "nurse";

        return {
          ...duty,
          attendedBy: {
            userID: { $oid: assignedStaffId },
            role: assignedStaffRole,
          },
          assignedStaffName,
        };
      });

      setSelectedPatient({
        ...first,
        dutyInfo: updatedDutyInfo,
      });
    } else {
      setSelectedPatient(first);
    }
  }, [orderedData, selectedPatient]);

  const isPhysiotherapist =
    selectedPatient?.specialistInfo?.role === "physiotherapist";

  const hasNoDutyInfo =
    !selectedPatient?.dutyInfo || selectedPatient?.dutyInfo.length === 0;

  const isPendingDutyRequest =
    !!selectedPatient && hasNoDutyInfo && isPendingStatus(selectedPatient);

  const isOneTimeDuty =
    String(selectedPatient?.dutyType || "").toLowerCase() === "onetime";

  const isRegularDuty =
    String(selectedPatient?.dutyType || "").toLowerCase() === "regular";

  const isNurse =
    String(selectedPatient?.specialistInfo?.role || "").toLowerCase() ===
    "nurse";
  const isPartnerAppointment =
    String(selectedPatient?.appointmentType?.type || "").toLowerCase() ===
    "partner";
  const pricingRequestStatus = String(selectedPatient?.pricingRequest || "")
    .trim()
    .toLowerCase();
  const isNurseRegularPartnerPricingPending =
    isNurse &&
    isRegularDuty &&
    isPartnerAppointment &&
    pricingRequestStatus === "pending";
  const isPhysioPartnerPricingPending =
    isPhysiotherapist &&
    isPartnerAppointment &&
    pricingRequestStatus === "pending";
  const isNurseOneTimePartnerPricingPending =
    isNurse &&
    isOneTimeDuty &&
    isPartnerAppointment &&
    pricingRequestStatus === "pending";
  const pricingRequestPendingMessage = isPhysiotherapist
    ? "The assessment fee is not configured under Services & Fees."
    : "The requested service/package is awaiting for pricing configuration.";

  const isPendingVisitStatus = isPendingStatus(selectedPatient);

  const hasMissingVisitInfo =
    !selectedPatient?.visitInfo?.startDate ||
    !selectedPatient?.visitInfo?.endDate ||
    !selectedPatient?.visitInfo?.slot;

  const activeAssessment = Array.isArray(selectedPatient?.assessment)
    ? selectedPatient.assessment.find((item) => item?.active)
    : null;

  const activeSessionFrequency = String(
    activeAssessment?.treatmentPlan?.sessionFrequency || "",
  ).trim();

  const normalizedSessionFrequency = normalizeSessionFrequency(
    activeSessionFrequency,
  );

  const isDailyOrAlternativeFrequency =
    normalizedSessionFrequency === "daily" ||
    normalizedSessionFrequency === "alternative";

  const isTwiceOrThriceFrequency =
    normalizedSessionFrequency === "twice" ||
    normalizedSessionFrequency === "thrice";

  const requiredWeekDayCount =
    normalizedSessionFrequency === "twice"
      ? 2
      : normalizedSessionFrequency === "thrice"
        ? 3
        : 0;

  const needsManualVisitInfo =
    isPendingDutyRequest &&
    isPendingVisitStatus &&
    (isPhysiotherapist || isOneTimeDuty || isRegularDuty) &&
    hasMissingVisitInfo;

  // ✅ IMPORTANT: in readOnly, never show manual inline inputs
  const showInlineManualVisitInputs = canEdit && needsManualVisitInfo;

  const existingStartDate = selectedPatient?.visitInfo?.startDate
    ? moment(selectedPatient.visitInfo.startDate).format("YYYY-MM-DD")
    : "";

  const effectiveManualStartDate = manualStartDate || existingStartDate;
  const effectiveManualSlot =
    manualSlot || selectedPatient?.visitInfo?.slot || "";

  const rawSessionCount = Number(selectedPatient?.visitInfo?.days);
  const effectiveSessionCount =
    Number.isFinite(rawSessionCount) && rawSessionCount > 0
      ? Math.floor(rawSessionCount)
      : 0;

  const physioExcludedDays = useMemo(() => {
    if (!isPhysiotherapist) return [];

    if (isTwiceOrThriceFrequency) {
      return WEEK_DAYS.filter((day) => !manualSelectedWeekDays.includes(day));
    }

    return [
      ...(manualExcludeSaturday ? ["Saturday"] : []),
      ...(manualExcludeSunday ? ["Sunday"] : []),
    ];
  }, [
    isPhysiotherapist,
    isTwiceOrThriceFrequency,
    manualSelectedWeekDays,
    manualExcludeSaturday,
    manualExcludeSunday,
  ]);

  const canComputePhysioSessions = useMemo(() => {
    if (
      !showInlineManualVisitInputs ||
      !isPhysiotherapist ||
      !effectiveManualStartDate ||
      effectiveSessionCount <= 0 ||
      !normalizedSessionFrequency
    ) {
      return false;
    }

    if (isTwiceOrThriceFrequency) {
      return manualSelectedWeekDays.length === requiredWeekDayCount;
    }

    return true;
  }, [
    showInlineManualVisitInputs,
    isPhysiotherapist,
    effectiveManualStartDate,
    effectiveSessionCount,
    normalizedSessionFrequency,
    isTwiceOrThriceFrequency,
    manualSelectedWeekDays.length,
    requiredWeekDayCount,
  ]);

  const computedPhysioSessionDurationDays = useMemo(() => {
    if (!canComputePhysioSessions) return [];

    return buildSessionDurationDays({
      startDate: effectiveManualStartDate,
      sessionCount: effectiveSessionCount,
      frequency: normalizedSessionFrequency,
      excludedDays: physioExcludedDays,
      selectedWeekDays: manualSelectedWeekDays,
    });
  }, [
    canComputePhysioSessions,
    effectiveManualStartDate,
    effectiveSessionCount,
    normalizedSessionFrequency,
    physioExcludedDays,
    manualSelectedWeekDays,
  ]);

  const computedPhysioEndDate = useMemo(() => {
    if (!computedPhysioSessionDurationDays.length) return "";
    return computedPhysioSessionDurationDays[
      computedPhysioSessionDurationDays.length - 1
    ];
  }, [computedPhysioSessionDurationDays]);

  const effectiveManualEndDate =
    isPhysiotherapist && needsManualVisitInfo
      ? computedPhysioEndDate
      : manualEndDate;

  const storedAvailabilitySessionDates = Array.isArray(
    activeAssessment?.treatmentPlan?.sessionDurationDays,
  )
    ? activeAssessment.treatmentPlan.sessionDurationDays
    : [];
  const effectiveAvailabilitySessionDates =
    computedPhysioSessionDurationDays.length > 0
      ? computedPhysioSessionDurationDays
      : storedAvailabilitySessionDates;
  const availabilitySessionKey = effectiveAvailabilitySessionDates.join("|");

  useEffect(() => {
    availabilityRequestIdRef.current += 1;
    setAvailabilityStaffList([]);
    setAvailabilityError("");
    setAvailabilityLoading(false);
    setAvailabilityContext(null);
    setAvailabilityModalOpen(false);
    setAssignStaffList([]);
  }, [
    selectedPatient?._id,
    selectedPatient?.specialistInfo?.role,
    selectedPatient?.specialistInfo?.subRole,
    selectedPatient?.visitInfo?.shiftType,
    effectiveManualStartDate,
    effectiveManualEndDate,
    effectiveManualSlot,
    availabilitySessionKey,
  ]);

  useEffect(() => {
    if (!availabilityModalOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") closeAvailabilityModal();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [availabilityModalOpen]);

  const availabilityGroups = useMemo(() => {
    const sortedSpecialists = [...availabilityStaffList].sort((left, right) =>
      getStaffDisplayName(left).localeCompare(getStaffDisplayName(right)),
    );

    return {
      free: sortedSpecialists.filter(
        (specialist) => specialist?.availability?.status !== "occupied",
      ),
      occupied: sortedSpecialists.filter(
        (specialist) => specialist?.availability?.status === "occupied",
      ),
    };
  }, [availabilityStaffList]);

  const availabilityDateLabel = useMemo(() => {
    const sessionDates = Array.isArray(availabilityContext?.sessionDates)
      ? availabilityContext.sessionDates.filter(Boolean)
      : [];
    if (sessionDates.length > 0) {
      const firstDate = formatAvailabilityDate(sessionDates[0]);
      const lastDate = formatAvailabilityDate(
        sessionDates[sessionDates.length - 1],
      );
      const dateRange =
        firstDate === lastDate ? firstDate : `${firstDate} - ${lastDate}`;
      return `${dateRange} (${sessionDates.length} session${
        sessionDates.length === 1 ? "" : "s"
      })`;
    }

    const startDate = formatAvailabilityDate(availabilityContext?.startDate);
    const endDate = formatAvailabilityDate(availabilityContext?.endDate);
    return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
  }, [availabilityContext]);

  const oneTimeManualSlotOptions = useMemo(() => {
    const options = [...ONE_TIME_MANUAL_SLOTS];
    if (selectedPatient?.visitInfo?.slot) {
      options.push(selectedPatient.visitInfo.slot);
    }
    return [
      ...new Set(
        options.map((item) => String(item || "").trim()).filter(Boolean),
      ),
    ];
  }, [selectedPatient?.visitInfo?.slot]);

  const physioManualSlotOptions = useMemo(() => {
    const options = [...PHYSIO_MANUAL_ASSIGN_SLOTS];
    if (selectedPatient?.visitInfo?.slot) {
      options.push(selectedPatient.visitInfo.slot);
    }
    return [
      ...new Set(
        options.map((item) => String(item || "").trim()).filter(Boolean),
      ),
    ];
  }, [selectedPatient?.visitInfo?.slot]);

  const regularLookupType = useMemo(
    () => getRegularLookupTypeByShift(selectedPatient?.visitInfo?.shiftType),
    [selectedPatient?.visitInfo?.shiftType],
  );

  const isTwentyFourBySevenRegularShift = useMemo(
    () => isTwentyFourBySevenShiftType(selectedPatient?.visitInfo?.shiftType),
    [selectedPatient?.visitInfo?.shiftType],
  );

  const regularManualSlotOptions = useMemo(() => {
    const options = [];

    if (regularLookupType) {
      lookupData
        .filter((item) => item?.type === regularLookupType)
        .forEach((item) => {
          options.push(...extractLookupSlotValues(item));
        });
    }

    if (selectedPatient?.visitInfo?.slot) {
      options.push(selectedPatient.visitInfo.slot);
    }

    return [
      ...new Set(
        options.map((item) => String(item || "").trim()).filter(Boolean),
      ),
    ];
  }, [lookupData, regularLookupType, selectedPatient?.visitInfo?.slot]);

  const manualTimeSlotOptions = useMemo(() => {
    if (isPhysiotherapist || (isNurse && isOneTimeDuty)) {
      const options = [];
      if (dynamicSlots && dynamicSlots.length > 0) {
        options.push(...dynamicSlots);
      }
      if (selectedPatient?.visitInfo?.slot) {
        options.push(selectedPatient.visitInfo.slot);
      }
      return [
        ...new Set(
          options.map((item) => String(item || "").trim()).filter(Boolean),
        ),
      ];
    }

    if (dynamicSlots && dynamicSlots.length > 0) {
      const options = [...dynamicSlots];
      if (selectedPatient?.visitInfo?.slot) {
        options.push(selectedPatient.visitInfo.slot);
      }
      return [
        ...new Set(
          options.map((item) => String(item || "").trim()).filter(Boolean),
        ),
      ];
    }

    if (isPhysiotherapist) return physioManualSlotOptions;
    if (isRegularDuty && isTwentyFourBySevenRegularShift) {
      return oneTimeManualSlotOptions;
    }
    return isRegularDuty ? regularManualSlotOptions : oneTimeManualSlotOptions;
  }, [
    isPhysiotherapist,
    isNurse,
    isOneTimeDuty,
    isRegularDuty,
    isTwentyFourBySevenRegularShift,
    physioManualSlotOptions,
    regularManualSlotOptions,
    oneTimeManualSlotOptions,
    dynamicSlots,
    selectedPatient?.visitInfo?.slot,
  ]);

  // console.log(selectedPatient);

  const dischargeSummariesForHistory = useMemo(() => {
    const summaryDoc = selectedPatient?.visitInfo?.dischargeSummaryDoc;
    if (!summaryDoc) return [];

    if (Array.isArray(summaryDoc)) {
      return summaryDoc.filter((item) => item && typeof item === "object");
    }

    return typeof summaryDoc === "object" ? [summaryDoc] : [];
  }, [selectedPatient?.visitInfo?.dischargeSummaryDoc]);

  useEffect(() => {
    if (!canEdit) return; // ✅ do not run in readOnly
    if (!showInlineManualVisitInputs || !isRegularDuty || isPhysiotherapist)
      return;

    if (isTwentyFourBySevenRegularShift) {
      setLookupError("");
      setLookupLoading(false);
      return;
    }

    if (!regularLookupType) {
      setLookupError("No slot mapping available for this shift type");
      return;
    }

    setLookupError("");

    if (lookupData.length > 0) return;

    let active = true;

    const loadLookup = async () => {
      try {
        setLookupLoading(true);
        setLookupError("");
        const result = await getLookupData();

        if (!active) return;

        if (result?.error === false && Array.isArray(result?.data)) {
          setLookupData(result.data);
        } else {
          setLookupData([]);
          setLookupError(result?.message || "Failed to load lookup slots");
        }
      } catch (error) {
        if (!active) return;
        setLookupData([]);
        setLookupError("Failed to load lookup slots");
      } finally {
        if (active) setLookupLoading(false);
      }
    };

    loadLookup();

    return () => {
      active = false;
    };
  }, [
    canEdit,
    showInlineManualVisitInputs,
    isRegularDuty,
    isPhysiotherapist,
    isTwentyFourBySevenRegularShift,
    regularLookupType,
    lookupData.length,
  ]);

  useEffect(() => {
    if (!canEdit) return; // ✅ do not run in readOnly
    if (!needsManualVisitInfo) return;

    if (isPhysiotherapist) {
      setManualEndDate(computedPhysioEndDate || "");
      return;
    }

    const computedEndDate = computeEndDateFromStartAndDays(
      effectiveManualStartDate,
      selectedPatient?.visitInfo?.days,
    );

    setManualEndDate(computedEndDate);
  }, [
    canEdit,
    needsManualVisitInfo,
    isPhysiotherapist,
    computedPhysioEndDate,
    effectiveManualStartDate,
    selectedPatient?.visitInfo?.days,
  ]);

  useEffect(() => {
    if (!isRegularDuty) {
      setLookupError("");
      setLookupLoading(false);
    }
  }, [isRegularDuty]);

  useEffect(() => {
    if (!canEdit) return;
    if (!showInlineManualVisitInputs) return;

    const isNurse =
      String(selectedPatient?.specialistInfo?.role || "").toLowerCase() ===
      "nurse";
    const targetCase = (isNurse && isOneTimeDuty) || isPhysiotherapist;

    if (!targetCase) {
      setDynamicSlots([]);
      return;
    }

    if (!effectiveManualStartDate) {
      setDynamicSlots([]);
      return;
    }

    let active = true;

    // console.log(selectedPatient);

    const fetchDynamicSlots = async () => {
      try {
        setDynamicSlotsLoading(true);
        setDynamicSlotsError("");

        const partnerID = selectedPatient?.appointmentType?.partnerID;

        const apiData = {
          role: isPhysiotherapist ? "physiotherpist" : "nurse",
          partnerID,
          startDate: moment(effectiveManualStartDate).format("DD/MM/YYYY"),
          currentTime: moment().format("hh:mm A"),
        };
        // console.log(apiData);
        let res;

        if (isPhysiotherapist) {
          res = await getphysioSlots(apiData);
        } else {
          res = await getOnetimeSlots(apiData);
        }

        // console.log("res", res);

        if (!active) return;

        if (res && res.error === false) {
          let slotValues = [];
          if (res?.data?.slots && Array.isArray(res.data.slots)) {
            // Check if nested by slotType
            const isNested = res.data.slots.some(
              (group) => group && Array.isArray(group.slots),
            );
            if (isNested) {
              res.data.slots.forEach((group) => {
                if (group && Array.isArray(group.slots)) {
                  group.slots.forEach((item) => {
                    if (item && item.slot) {
                      slotValues.push(item.slot);
                    }
                  });
                }
              });
            } else {
              // Flat array of slots inside res.data.slots
              res.data.slots.forEach((item) => {
                if (item) {
                  if (typeof item === "string") {
                    slotValues.push(item);
                  } else if (item.slot) {
                    slotValues.push(item.slot);
                  } else if (item.label) {
                    slotValues.push(item.label);
                  }
                }
              });
            }
          } else {
            // Check flat array in res.data or res.slots
            let flatArray = [];
            if (Array.isArray(res?.data)) {
              flatArray = res.data;
            } else if (Array.isArray(res?.slots)) {
              flatArray = res.slots;
            }

            flatArray.forEach((item) => {
              if (item) {
                if (typeof item === "string") {
                  slotValues.push(item);
                } else if (item.slot) {
                  slotValues.push(item.slot);
                } else if (item.label) {
                  slotValues.push(item.label);
                }
              }
            });
          }

          const filteredSlots = slotValues
            .map((s) => String(s || "").trim())
            .filter(Boolean);

          setDynamicSlots(filteredSlots);
        } else {
          setDynamicSlotsError(res?.message || "Failed to fetch slots");
          setDynamicSlots([]);
        }
      } catch (err) {
        console.error("Error fetching dynamic slots:", err);
        if (active) {
          setDynamicSlotsError("Error loading slots");
          setDynamicSlots([]);
        }
      } finally {
        if (active) {
          setDynamicSlotsLoading(false);
        }
      }
    };

    fetchDynamicSlots();

    return () => {
      active = false;
    };
  }, [
    effectiveManualStartDate,
    selectedPatient,
    isPhysiotherapist,
    isOneTimeDuty,
    showInlineManualVisitInputs,
    canEdit,
  ]);

  const isManualVisitInfoValid =
    !needsManualVisitInfo ||
    (Boolean(effectiveManualStartDate) &&
      Boolean(effectiveManualEndDate) &&
      Boolean(effectiveManualSlot) &&
      (!isPhysiotherapist ||
        computedPhysioSessionDurationDays.length === effectiveSessionCount) &&
      (!isPhysiotherapist ||
        !isTwiceOrThriceFrequency ||
        manualSelectedWeekDays.length === requiredWeekDayCount) &&
      (!isRegularDuty || !lookupLoading));

  const previewSessionDurationDays = useMemo(() => {
    if (!showInlineManualVisitInputs || !isPhysiotherapist) return [];
    return computedPhysioSessionDurationDays;
  }, [
    showInlineManualVisitInputs,
    isPhysiotherapist,
    computedPhysioSessionDurationDays,
  ]);

  const previewSessionDaySet = useMemo(
    () => new Set(previewSessionDurationDays),
    [previewSessionDurationDays],
  );

  const calendarPreviewMonths = useMemo(() => {
    if (
      !showInlineManualVisitInputs ||
      !isPhysiotherapist ||
      previewSessionDurationDays.length === 0
    ) {
      return [];
    }

    const start = moment(
      previewSessionDurationDays[0],
      "YYYY-MM-DD",
      true,
    ).startOf("month");

    const end = moment(
      previewSessionDurationDays[previewSessionDurationDays.length - 1],
      "YYYY-MM-DD",
      true,
    ).startOf("month");

    if (!start.isValid() || !end.isValid() || start.isAfter(end, "month")) {
      return [];
    }

    const months = [];
    const cursor = start.clone();
    let safetyCounter = 0;

    while (cursor.isSameOrBefore(end, "month") && safetyCounter < 12) {
      months.push({
        key: cursor.format("YYYY-MM"),
        label: cursor.format("MMMM YYYY"),
        cells: buildMonthCalendarCells(cursor),
      });
      cursor.add(1, "month");
      safetyCounter += 1;
    }

    return months;
  }, [
    showInlineManualVisitInputs,
    isPhysiotherapist,
    previewSessionDurationDays,
  ]);

  useEffect(() => {
    if (!showCalendarPreview || calendarPreviewMonths.length === 0) {
      setCalendarPreviewMonthKey("");
      return;
    }

    const exists = calendarPreviewMonths.some(
      (month) => month.key === calendarPreviewMonthKey,
    );

    if (!exists) {
      setCalendarPreviewMonthKey(calendarPreviewMonths[0].key);
    }
  }, [showCalendarPreview, calendarPreviewMonths, calendarPreviewMonthKey]);

  const activeCalendarMonth = useMemo(() => {
    if (!calendarPreviewMonths.length) return null;
    return (
      calendarPreviewMonths.find(
        (month) => month.key === calendarPreviewMonthKey,
      ) || calendarPreviewMonths[0]
    );
  }, [calendarPreviewMonths, calendarPreviewMonthKey]);

  const activeCalendarMonthIndex = useMemo(() => {
    if (!activeCalendarMonth) return -1;
    return calendarPreviewMonths.findIndex(
      (month) => month.key === activeCalendarMonth.key,
    );
  }, [calendarPreviewMonths, activeCalendarMonth]);

  const canGoPrevCalendarMonth = activeCalendarMonthIndex > 0;
  const canGoNextCalendarMonth =
    activeCalendarMonthIndex >= 0 &&
    activeCalendarMonthIndex < calendarPreviewMonths.length - 1;

  const getColumns = () => {
    const cols = [
      {
        Header: () => (
          <div className="font-poppins ml-4 whitespace-nowrap">Name</div>
        ),
        accessor: "patient.name",
        Cell: ({ row: { original } }) => (
          <div className="font-poppins flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                selectedPatient?._id === original._id
                  ? "bg-blue-600"
                  : "bg-transparent"
              }`}
            />
            <p className="text-blue-600 truncate max-w-[200px]">
              {original?.patient?.name || "N/A"}
            </p>
          </div>
        ),
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap">Gender</div>
        ),
        accessor: "patient.gender",
        Cell: ({ row: { original } }) => (
          <div className="font-poppins">
            {original?.patient?.gender
              ? capitalizeFirstLetter(original.patient.gender)
              : "-"}
          </div>
        ),
      },
      {
        Header: () => <div className="font-poppins whitespace-nowrap">Age</div>,
        accessor: "patient.dob",
        Cell: ({ row: { original } }) => (
          <div className="font-poppins">
            {calculateAge(
              original?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.dob ||
                null,
            )}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="flex justify-start items-center text-[13px] font-poppins text-left whitespace-nowrap">
            Service
          </div>
        ),
        accessor: "specialistInfo.role",
        Cell: ({ row: { original } }) => (
          <div className="flex justify-start items-center text-left text-[12px] max-w-[150px] font-poppins truncate">
            {original?.specialistInfo?.role
              ? capitalizeFirstLetter(original.specialistInfo.role)
              : "-"}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="flex justify-start items-center text-[13px] font-poppins text-left whitespace-nowrap">
            Location
          </div>
        ),
        accessor: "visitInfo.addressInfo.city",
        Cell: ({ row: { original } }) => (
          <div className="flex justify-start items-center text-center text-[12px] font-poppins">
            {getLocationDisplay(original)}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="flex justify-start items-center text-[13px] font-poppins text-left whitespace-nowrap">
            Duty Type
          </div>
        ),
        accessor: "visitInfo.shiftType",
        Cell: ({ row: { original } }) => (
          <div className="flex justify-start items-center text-left text-[12px] max-w-[170px] font-poppins truncate">
            {getDutyTypeDisplay(original)}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="flex justify-start items-center text-[13px] font-poppins text-left whitespace-nowrap">
            Days
          </div>
        ),
        accessor: "visitInfo.days",
        Cell: ({ row: { original } }) => {
          const days = Number(original?.visitInfo?.days);
          return (
            <div className="flex justify-start items-center text-left text-[12px] font-poppins whitespace-nowrap">
              {Number.isFinite(days) ? days : "-"}
            </div>
          );
        },
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap ml-2">Status</div>
        ),
        accessor: "status",
        Cell: ({ row: { original } }) => {
          const dutyStatus = isActionRequiredBooking(original)
            ? "Action Required"
            : getDutyStatus(original);
          return (
            <div className="flex justify-start text-center">
              <p className="text-center">
                <StatusBadge status={dutyStatus} />
              </p>
            </div>
          );
        },
      },
      ...(readOnly
        ? [
            {
              Header: () => (
                <div className="font-poppins whitespace-nowrap text-center">
                  Timeline
                </div>
              ),
              accessor: "timeline",
              disableSortBy: true,
              Cell: ({ row: { original } }) => {
                const visit = original;
                const timelineData = visit?.timeline || [];
                const hasTimeline =
                  Array.isArray(timelineData) && timelineData.length > 0;
                return (
                  <div className="flex justify-center">
                    <button
                      type="button"
                      disabled={!hasTimeline}
                      onClick={(e) => {
                        e.stopPropagation();
                        openTimelineModal(visit);
                      }}
                      className={`text-[#4361EE] underline font-medium font-poppins text-[13px] ${!hasTimeline ? "opacity-40 cursor-not-allowed" : "hover:opacity-80"}`}
                    >
                      View
                    </button>
                  </div>
                );
              },
            },
          ]
        : []),
      ...(readOnly
        ? []
        : [
            {
              Header: () => (
                <div className="font-poppins whitespace-nowrap text-center">
                  Action
                </div>
              ),
              accessor: "deleteAction",
              Cell: ({ row: { original } }) => {
                const statusStr = String(original?.status || "")
                  .toLowerCase()
                  .trim()
                  .replace(/[\s_-]+/g, "");
                const dutyStatus = getDutyStatus(original);
                const isDeletable =
                  statusStr === "pending" ||
                  statusStr === "paymentpending" ||
                  dutyStatus === "Payment Pending";

                if (!isDeletable)
                  return <div className="flex justify-center">-</div>;

                return (
                  <div className="flex justify-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteVisit(original._id);
                      }}
                      className="p-1.5 rounded-full hover:bg-red-50 text-red-500 transition-colors"
                      title="Cancel Booking"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                );
              },
              disableSortBy: true,
            },
          ]),
    ];

    return cols;
  };

  // ✅ include readOnly so columns re-render when switching mode
  const columns = useMemo(() => getColumns(), [selectedPatient, readOnly]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    setPageSize,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: orderedData,
      initialState: { pageSize: selectedPageCount, pageIndex: paginationPage },
      autoResetPage: false,
    },
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    if (pageIndex !== paginationPage) setPaginationPage(pageIndex);
  }, [pageIndex, paginationPage]);

  const handlePageCountChange = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return;
    localStorage.setItem("bookingsPageCount", String(n));
    setSelectedPageCount(n);
    setPageSize(n);
    setPaginationPage(0);
    gotoPage(0);
  };

  if (loading) {
    return (
      <div>
        <Loader loading />
      </div>
    );
  }

  if (!loading && visitList.length === 0) {
    return (
      <div className="flex flex-col w-full h-[100vh] gap-3 p-2">
        <div className="bg-white rounded-2xl shadow-md border border-[#E3E7F0] flex flex-col justify-center items-center w-[100%] h-[85vh]">
          <div className="w-14 h-14 rounded-full bg-[#EEF2FF] flex items-center justify-center mb-4">
            <img
              src={CommonImages.noDuty}
              className="h-[125px] w-[160px] object-contain"
              alt="No Duty"
            />
          </div>
          <h2 className="text-[14px] font-semibold font-poppins text-[#787998] mb-1">
            No visits available
          </h2>
        </div>
      </div>
    );
  }

  const openTimeModal = (dutyIndex, dutyKey, currentSlot) => {
    if (!isPhysiotherapist || readOnly) return; // ✅ blocked in readOnly

    const alertMsg =
      "Alert: Booking Slot Change Notice\n\n" +
      "You are attempting to change the scheduled booking slot for an assigned visit.\n" +
      "Please ensure that any changes are made only after clear communication and mutual agreement between you and the patient.\n\n" +
      'Kindly note that this process is solely between you and the patient. We ("Ceviliyar") do not participate in, monitor, or assume responsibility for any booking slot changes made.';

    const proceed = window.confirm(alertMsg);
    if (!proceed) return;

    setTimeModalDutyIndex(dutyIndex);
    setTimeModalDutyKey(dutyKey);
    setTimeModalSelectedSlot(
      currentSlot || selectedPatient?.visitInfo?.slot || PHYSIO_TIME_SLOTS[0],
    );
    setTimeModalOpen(true);
  };

  const closeTimeModal = () => {
    setTimeModalOpen(false);
    setTimeModalDutyIndex(null);
    setTimeModalDutyKey(null);
    setTimeModalSelectedSlot("");
  };

  const applySelectedTimeSlot = async () => {
    if (readOnly) return; // ✅ block in readOnly
    if (
      !selectedPatient ||
      timeModalDutyIndex == null ||
      !timeModalSelectedSlot
    )
      return;

    const dutyForModal = selectedPatient.dutyInfo?.[timeModalDutyIndex] || null;
    if (!dutyForModal) return;

    const dutyDateRaw = dutyForModal.date;
    const dateStr = moment(dutyDateRaw).format("YYYY-MM-DD");
    const { startTimeStr, endTimeStr } = parseSelectedSlot(
      timeModalSelectedSlot,
    );

    if (!startTimeStr || !endTimeStr) {
      toast.error("Invalid time slot selected");
      return;
    }

    const startMoment = moment(
      `${dateStr} ${startTimeStr}`,
      "YYYY-MM-DD hh:mm A",
    );
    const endMoment = moment(`${dateStr} ${endTimeStr}`, "YYYY-MM-DD hh:mm A");

    const startTime = startMoment.format("YYYY-MM-DDTHH:mm:ss.SSSZ");
    const endTime = endMoment.format("YYYY-MM-DDTHH:mm:ss.SSSZ");

    const payload = {
      visitID: selectedPatient._id,
      startTime: moment(startTime).utc().format(),
      endTime: moment(endTime).utc().format(),
      date: dateStr,
    };

    try {
      const response = await timeSlotChange(payload);

      if (response?.error !== true) {
        toast.success("Time slot updated successfully");

        if (timeModalDutyKey) {
          setDutySlots((prev) => ({
            ...prev,
            [timeModalDutyKey]: timeModalSelectedSlot,
          }));
        }

        closeTimeModal();
      } else {
        toast.error("Failed to update time slot");
      }
    } catch (err) {
      console.error("Error updating time slot:", err);
      toast.error("Something went wrong while updating time slot");
    }
  };

  const dutyForModal =
    timeModalDutyIndex != null
      ? selectedPatient?.dutyInfo?.[timeModalDutyIndex]
      : null;

  const isPhysioNoAssessment =
    isPhysiotherapist && selectedPatient?.isAssessment === false;

  const assessmentLabel = isPhysioNoAssessment
    ? "Duty Staff"
    : "Assessment Staff";
  const dutyLabel = "Duty Staff";
  const standby1Label = "Standby Staff 1";
  const physioStandbyLabel = isPhysioNoAssessment
    ? "Standby Staff 1"
    : "Standby Staff";
  const standby2Label = "Standby Staff 2";

  const tableStaffHeader =
    isPhysiotherapist &&
    (!selectedPatient?.dutyInfo || selectedPatient.dutyInfo.length === 0)
      ? assessmentLabel
      : "Assigned Staff";

  const minStartDate = moment().startOf("day").format("YYYY-MM-DD");

  // ✅ In readOnly: ALWAYS show text instead of dropdowns/inputs
  const showStaticAssignment = readOnly || isRequestedStatus(selectedPatient);
  const hasAvailabilityDate = moment(
    effectiveManualStartDate,
    "YYYY-MM-DD",
    true,
  ).isValid();
  const canCheckStaffAvailability =
    canEdit &&
    !showStaticAssignment &&
    isPendingStatus(selectedPatient) &&
    selectedPatient &&
    (!selectedPatient?.dutyInfo || selectedPatient.dutyInfo.length === 0);

  const renderAvailabilityButton = () =>
    canCheckStaffAvailability ? (
      <button
        type="button"
        onClick={openAvailabilityModal}
        disabled={!hasAvailabilityDate}
        title={
          hasAvailabilityDate
            ? "Check Duty Staff availability"
            : "Select a date to check availability"
        }
        className="inline-flex items-center gap-1 rounded-full border border-[#4361EE]/30 bg-white px-2 py-1 text-[10px] font-semibold text-[#4361EE] font-poppins transition-colors hover:bg-[#EEF2FF] disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
        aria-label="Check Duty Staff availability"
      >
        <CalendarCheck2 className="h-3.5 w-3.5" aria-hidden="true" />
        Availability
      </button>
    ) : null;

  return (
    <div className="flex flex-col h-[100vh] overflow-y-auto gap-3 overflow-x-hidden pb-10">
      <div className="flex flex-col min-[1100px]:flex-row items-stretch justify-start w-full min-[1100px]:flex-1 min-[1100px]:min-h-0 h-auto gap-3 p-2 max-w-full min-w-0">
        <div className="min-[1100px]:flex-1 flex flex-col gap-3 min-[1100px]:min-h-0 min-w-0 w-full max-w-full h-auto">
          <div className="bg-white rounded-2xl shadow-lg p-2 min-[1100px]:flex-[1.1] min-[1100px]:min-h-0 min-w-0 h-auto flex flex-col justify-between">
            <div className="flex justify-between items-center p-2 border-b border-gray-100 flex-shrink-0">
              <p className="text-[15px] font-semibold font-poppins whitespace-nowrap">
                Patient List ({filteredData.length})
              </p>

              <div className="flex items-center gap-2">
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => getVisit()}
                    className="flex items-center justify-center bg-white text-blue-600 border border-blue-600 rounded-md h-[32px] w-[32px] hover:bg-blue-50 transition-colors"
                    style={{ borderWidth: 1.5 }}
                    title="Refresh Data"
                  >
                    <RotateCcw size={16} />
                  </button>
                )}
                <div
                  className="flex items-center bg-white border border-blue-600 rounded-md px-2 h-[32px]"
                  style={{ borderWidth: 1.5 }}
                >
                  <select
                    value={selectedPageCount}
                    onChange={(e) => handlePageCountChange(e.target.value)}
                    className="outline-none font-poppins text-[12px] text-blue-600 bg-transparent font-medium"
                  >
                    {[25, 50, 75, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>

                <select
                  value={requestFilter}
                  onChange={(e) => {
                    setPaginationPage(0);
                    setRequestFilter(e.target.value);
                  }}
                  className="border rounded-md text-[12px] text-blue-600 border-blue-600 py-1 px-3"
                  style={{ borderWidth: 1.5 }}
                >
                  {requestFilterOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleExportDetailedPdf}
                  disabled={exportingPdf || loading}
                  className="flex items-center gap-1.5 bg-blue-600 text-white border border-blue-600 rounded-md px-2.5 h-[32px] text-[12px] font-poppins font-semibold hover:bg-blue-700 transition-colors disabled:cursor-not-allowed disabled:bg-blue-300 disabled:border-blue-300"
                  style={{ borderWidth: 1.5 }}
                  title="Export accepted, startDuty, endDuty and completed bookings as detailed PDF"
                >
                  {exportingPdf ? "Exporting..." : "Export PDF"}
                </button>
              </div>
            </div>

            <div className="overflow-auto flex-1 min-h-0 w-full max-w-full scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
              <table
                {...getTableProps()}
                className="w-full min-w-[1000px]"
                style={{ minWidth: "1000px" }}
              >
                <thead className="bg-[#F6F8FC] sticky top-0 z-10">
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps(),
                          )}
                          className="px-2 py-1.5 text-left text-[12px] font-medium text-[#8177A8] tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                        >
                          <div className="flex items-center gap-1">
                            {column.render("Header")}
                            {column.canSort && (
                              <SortIcon
                                isSorted={column.isSorted}
                                isSortedDesc={column.isSortedDesc}
                              />
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody
                  {...getTableBodyProps()}
                  className="bg-white divide-y divide-gray-200"
                >
                  {tablePage.map((row) => {
                    prepareRow(row);
                    const isSelected =
                      selectedPatient?._id === row.original._id;

                    return (
                      <tr
                        {...row.getRowProps()}
                        onClick={() => handleRowClick(row.original)}
                        className={`cursor-pointer transition-colors ${
                          isSelected
                            ? "bg-[#DFEAFF] border-l-4 border-blue-600"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            className="px-2 py-1 text-[12px] text-gray-700 whitespace-nowrap"
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}

                  {tablePage.length === 0 && (
                    <tr>
                      <td
                        colSpan={headerGroups[0]?.headers?.length || 1}
                        className="text-center text-[12px] text-gray-500 py-6 font-poppins"
                      >
                        No records available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredData.length > 0 && (
              <div className="mt-2 mb-1 flex-shrink-0">
                <Page
                  paginationPage={paginationPage}
                  nextPage={nextPage}
                  previousPage={previousPage}
                  canPreviousPage={canPreviousPage}
                  canNextPage={canNextPage}
                  pageOptions={pageOptions}
                  screen="hospital"
                />
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-3 min-[1100px]:flex-[0.9] min-[1100px]:min-h-0 h-auto flex flex-col min-w-0 w-full max-w-full">
            <>
              <h3 className="text-[15px] font-semibold font-poppins text-gray-800 mb-3 whitespace-nowrap">
                Assign staff
              </h3>

              {!selectedPatient ? (
                <div className="flex-1 flex items-center justify-center text-[12px] text-gray-500 font-poppins">
                  No visit data available
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-x-auto overflow-y-auto w-full max-w-full scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
                    <table className="w-full min-w-[800px] border border-gray-200 rounded-[12px] overflow-hidden">
                      <thead className="bg-[#F6F8FC]">
                        <tr>
                          <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                            {showInlineManualVisitInputs
                              ? "Start Date"
                              : "Service Date"}
                          </th>

                          {showInlineManualVisitInputs && (
                            <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                              End Date
                            </th>
                          )}

                          <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                            Time
                          </th>

                          <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span>{tableStaffHeader}</span>
                              {(!isPhysiotherapist || isPhysioNoAssessment) &&
                                renderAvailabilityButton()}
                            </div>
                          </th>
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedPatient.dutyInfo &&
                        selectedPatient.dutyInfo.length > 0 ? (
                          selectedPatient.dutyInfo.map((duty, index) => {
                            const standbyStaffList = getStandbyStaffList();
                            const totalDays =
                              selectedPatient.dutyInfo.length || 0;
                            const currentDayNumber =
                              duty.day != null ? duty.day : index + 1;
                            const dutyCompleted = isDutyCompleted(duty);
                            const dutyKey = duty._id || index;
                            const displaySlot =
                              dutySlots[dutyKey] ||
                              duty.slot ||
                              selectedPatient?.visitInfo?.slot ||
                              "-";

                            return (
                              <tr
                                key={duty._id || index}
                                className="hover:bg-[#DFEAFF]"
                              >
                                <td className="px-4 py-1 text-[12px] text-gray-700 font-medium font-poppins whitespace-nowrap">
                                  {moment(duty.date).format(
                                    "DD/MM/YYYY - dddd",
                                  )}
                                  {totalDays > 0 && (
                                    <span className="ml-1 text-[11px] text-gray-500">
                                      ({currentDayNumber}/{totalDays})
                                    </span>
                                  )}
                                </td>

                                <td className="px-4 py-1 text-[12px] text-gray-700 font-poppins whitespace-nowrap">
                                  <div className="inline-flex items-center gap-2">
                                    <span className="text-[12px] font-medium text-gray-800">
                                      {displaySlot}
                                    </span>
                                    {/* 
                                    {isPhysiotherapist && !dutyCompleted && canEdit && (
                                      <button
                                        onClick={() =>
                                          openTimeModal(index, dutyKey, displaySlot)
                                        }
                                        className="text-[11px] text-blue-600 px-2 py-[2px] font-poppins"
                                      >
                                        <img
                                          src={CommonImages.editIconBlue}
                                          className="text-[#4361EE] w-6 h-6"
                                          alt="Edit"
                                        />
                                      </button>
                                    )} */}
                                  </div>
                                </td>

                                <td className="px-4 py-1 whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    {canEdit && editingDutyIndex === index ? (
                                      <div className="relative flex-1 flex items-center gap-2">
                                        <select
                                          value={changingDutyStaff[index] || ""}
                                          onChange={(e) => {
                                            if (dutyCompleted || readOnly)
                                              return;
                                            setChangingDutyStaff({
                                              ...changingDutyStaff,
                                              [index]: e.target.value,
                                            });
                                            handleDutyStaffChange(
                                              index,
                                              e.target.value,
                                            );
                                          }}
                                          autoFocus
                                          disabled={dutyCompleted || readOnly}
                                          className="w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 pr-8 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                          <option value="" hidden>
                                            Select staff
                                          </option>
                                          {standbyStaffList.map((staff) => (
                                            <option
                                              key={staff.id}
                                              value={staff.id}
                                            >
                                              {staff.name}
                                            </option>
                                          ))}
                                        </select>

                                        {!dutyCompleted && (
                                          <>
                                            <div className="pointer-events-none absolute inset-y-0 right-10 flex items-center px-2 text-gray-700">
                                              <svg
                                                className="fill-current h-3 w-3"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                              >
                                                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                              </svg>
                                            </div>

                                            <button
                                              onClick={() =>
                                                handleCloseDropdown(index)
                                              }
                                              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                                            >
                                              <svg
                                                className="w-4 h-4 text-gray-600"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M6 18L18 6M6 6l12 12"
                                                />
                                              </svg>
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    ) : (
                                      <>
                                        <div className="flex items-center gap-2">
                                          <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-[14px]">
                                            <img
                                              src={CommonImages.nurseNew}
                                              alt="Avatar"
                                              className="w-7 h-7 object-cover rounded-full"
                                            />
                                          </div>

                                          <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[160px]">
                                            {duty?.attendedBy?.userID
                                              ?.personalInfo?.basicInfo
                                              ?.nameInfo?.title &&
                                            duty?.attendedBy?.userID
                                              ?.personalInfo?.basicInfo
                                              ?.nameInfo?.fullName
                                              ? `${duty.attendedBy.userID.personalInfo.basicInfo.nameInfo.title} ${duty.attendedBy.userID.personalInfo.basicInfo.nameInfo.fullName}`
                                              : "N/A"}
                                          </span>
                                        </div>

                                        {/* {canEdit &&
                                          standbyStaffList.length > 0 &&
                                          !dutyCompleted && (
                                            <button
                                              onClick={() => setEditingDutyIndex(index)}
                                              className="p-1.5 hover:bg-blue-50 rounded-full transition-colors"
                                            >
                                              <img
                                                src={CommonImages.editIconBlue}
                                                className="text-[#4361EE] w-6 h-6"
                                                alt="Edit"
                                              />
                                            </button>
                                          )} */}
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr className="hover:bg-[#DFEAFF]">
                            <td className="px-4 py-1 text-[12px] text-gray-700 font-medium font-poppins whitespace-nowrap">
                              {showInlineManualVisitInputs ? (
                                <input
                                  type="date"
                                  value={effectiveManualStartDate || ""}
                                  min={
                                    selectedPatient?.dutyType === "regular" &&
                                    selectedPatient?.specialistInfo?.role ===
                                      "nurse"
                                      ? moment()
                                          .add(1, "day")
                                          .format("YYYY-MM-DD") // tomorrow
                                      : moment().format("YYYY-MM-DD") // today
                                  }
                                  onChange={(e) => {
                                    const val = e.target.value;

                                    if (
                                      selectedPatient?.dutyType === "regular" &&
                                      selectedPatient?.specialistInfo?.role ===
                                        "nurse"
                                    ) {
                                      // Block today and past — only allow tomorrow onwards
                                      if (
                                        val &&
                                        moment(val).isSameOrBefore(
                                          moment(),
                                          "day",
                                        )
                                      )
                                        return;
                                    } else {
                                      // Allow today — only block past dates
                                      if (
                                        val &&
                                        moment(val).isBefore(moment(), "day")
                                      )
                                        return;
                                    }

                                    setManualStartDate(val);
                                    setManualSlot("");
                                    setIsEditingStaff(true);
                                  }}
                                  disabled={!isPendingStatus(selectedPatient)}
                                  className="w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins border border-gray-300 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                                />
                              ) : (
                                getServiceDateDisplay(
                                  selectedPatient?.visitInfo,
                                )
                              )}
                            </td>

                            {showInlineManualVisitInputs && (
                              <td className="px-4 py-1 text-[12px] text-gray-700 font-poppins whitespace-nowrap">
                                <input
                                  type="date"
                                  value={effectiveManualEndDate}
                                  readOnly
                                  disabled
                                  className="w-full bg-gray-100 rounded-md px-2 py-1.5 text-[12px] font-poppins border border-gray-300 outline-none cursor-not-allowed"
                                />
                              </td>
                            )}

                            <td className="px-4 py-1 text-[12px] text-gray-700 font-poppins whitespace-nowrap">
                              {showInlineManualVisitInputs ? (
                                <div className="relative w-full">
                                  <select
                                    value={effectiveManualSlot || ""}
                                    onChange={(e) => {
                                      setManualSlot(e.target.value);
                                      setIsEditingStaff(true);
                                    }}
                                    disabled={
                                      !isPendingStatus(selectedPatient) ||
                                      (isRegularDuty &&
                                        (lookupLoading ||
                                          manualTimeSlotOptions.length ===
                                            0)) ||
                                      (((isNurse && isOneTimeDuty) ||
                                        isPhysiotherapist) &&
                                        (dynamicSlotsLoading ||
                                          manualTimeSlotOptions.length === 0))
                                    }
                                    className="w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    <option value="" hidden>
                                      {(isRegularDuty && lookupLoading) ||
                                      dynamicSlotsLoading
                                        ? "Loading slots..."
                                        : "Select slot"}
                                    </option>
                                    {manualTimeSlotOptions.map((slotValue) => (
                                      <option key={slotValue} value={slotValue}>
                                        {slotValue}
                                      </option>
                                    ))}
                                  </select>

                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                    <svg
                                      className="fill-current h-3 w-3"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                    </svg>
                                  </div>
                                </div>
                              ) : (
                                selectedPatient?.visitInfo?.slot ||
                                manualSlot ||
                                "-"
                              )}
                            </td>

                            <td className="px-4 py-1 whitespace-nowrap">
                              {showStaticAssignment ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-[14px]">
                                    <img
                                      src={CommonImages.nurseNew}
                                      alt="Avatar"
                                      className="w-7 h-7 object-cover rounded-full"
                                    />
                                  </div>
                                  <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[160px]">
                                    {isPhysiotherapist
                                      ? getAssessmentHCPName(selectedPatient)
                                      : getAssignedHCPName(selectedPatient)}
                                  </span>
                                </div>
                              ) : (
                                <div className="relative w-full">
                                  <select
                                    value={
                                      isPhysiotherapist
                                        ? assessmentStaff || ""
                                        : selectedMainStaff || ""
                                    }
                                    onChange={(e) => {
                                      if (isPhysiotherapist) {
                                        setAssessmentStaff(e.target.value);
                                        setSelectedMainStaff(e.target.value);
                                        setIsEditingStaff(true);
                                        if (selectedStandby1 === e.target.value)
                                          setSelectedStandby1(null);
                                        if (selectedStandby2 === e.target.value)
                                          setSelectedStandby2(null);
                                      } else {
                                        handleSingleStaffChange(e.target.value);
                                      }
                                    }}
                                    onFocus={async () => {
                                      await getNames();
                                    }}
                                    disabled={!isPendingStatus(selectedPatient)}
                                    className={`w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 ${
                                      !isPendingStatus(selectedPatient)
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                  >
                                    <option value="" hidden>
                                      {isPhysiotherapist
                                        ? `Select ${assessmentLabel}`
                                        : "Select staff"}
                                    </option>
                                    {assignStaffList.map((staff) => (
                                      <option key={staff._id} value={staff._id}>
                                        {getStaffDisplayName(staff)}
                                      </option>
                                    ))}
                                  </select>

                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                    <svg
                                      className="fill-current h-3 w-3"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {showInlineManualVisitInputs &&
                      !isPhysiotherapist &&
                      isRegularDuty &&
                      (lookupError ||
                        (!lookupLoading &&
                          regularLookupType &&
                          manualTimeSlotOptions.length === 0)) && (
                        <p className="text-[11px] text-red-500 font-poppins mt-2">
                          {lookupError ||
                            "No slots available for selected shift type"}
                        </p>
                      )}

                    {showInlineManualVisitInputs &&
                      ((isNurse && isOneTimeDuty) || isPhysiotherapist) &&
                      (dynamicSlotsError ||
                        (!dynamicSlotsLoading &&
                          effectiveManualStartDate &&
                          manualTimeSlotOptions.length === 0)) && (
                        <p className="text-[11px] text-red-500 font-poppins mt-2">
                          {dynamicSlotsError ||
                            "No slots available for this date"}
                        </p>
                      )}

                    {showInlineManualVisitInputs &&
                      isPhysiotherapist &&
                      (isDailyOrAlternativeFrequency ||
                        isTwiceOrThriceFrequency) && (
                        <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                          <div className="border border-gray-200 rounded-[12px] p-3">
                            <p className="text-[12px] font-semibold text-[#1D1D22] font-poppins mb-2">
                              Session Frequency: {activeSessionFrequency || "-"}
                            </p>

                            {isDailyOrAlternativeFrequency && (
                              <div className="flex flex-wrap gap-4">
                                <label className="inline-flex items-center gap-2 text-[12px] text-gray-700 font-poppins">
                                  <input
                                    type="checkbox"
                                    checked={manualExcludeSaturday}
                                    onChange={(e) => {
                                      setManualExcludeSaturday(
                                        e.target.checked,
                                      );
                                      setIsEditingStaff(true);
                                    }}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    disabled={!isPendingStatus(selectedPatient)}
                                  />
                                  Exclude Saturday
                                </label>

                                <label className="inline-flex items-center gap-2 text-[12px] text-gray-700 font-poppins">
                                  <input
                                    type="checkbox"
                                    checked={manualExcludeSunday}
                                    onChange={(e) => {
                                      setManualExcludeSunday(e.target.checked);
                                      setIsEditingStaff(true);
                                    }}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    disabled={!isPendingStatus(selectedPatient)}
                                  />
                                  Exclude Sunday
                                </label>
                              </div>
                            )}

                            {isTwiceOrThriceFrequency && (
                              <>
                                <p className="text-[11px] text-gray-500 font-poppins mb-2">
                                  Select {requiredWeekDayCount} day
                                  {requiredWeekDayCount > 1 ? "s" : ""} for
                                  sessions
                                </p>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                  {WEEK_DAYS.map((day) => {
                                    const selected =
                                      manualSelectedWeekDays.includes(day);
                                    const maxSelectedReached =
                                      !selected &&
                                      requiredWeekDayCount > 0 &&
                                      manualSelectedWeekDays.length >=
                                        requiredWeekDayCount;

                                    return (
                                      <label
                                        key={day}
                                        className={`inline-flex items-center gap-2 px-2 py-1 rounded-md border text-[12px] font-poppins ${
                                          selected
                                            ? "border-blue-500 bg-blue-50 text-blue-700"
                                            : "border-gray-200 text-gray-700"
                                        } ${
                                          maxSelectedReached ? "opacity-60" : ""
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selected}
                                          onChange={() =>
                                            handleManualWeekDayToggle(day)
                                          }
                                          disabled={
                                            !isPendingStatus(selectedPatient) ||
                                            maxSelectedReached
                                          }
                                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        {day.slice(0, 3)}
                                      </label>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </div>

                          <div className="border border-gray-200 rounded-[12px] p-3">
                            <button
                              type="button"
                              onClick={() =>
                                setShowCalendarPreview((prev) => !prev)
                              }
                              className="w-full flex items-center justify-between text-left px-2 py-1.5 rounded-md border border-gray-200 bg-[#F8FAFF] hover:bg-[#EEF3FF] transition-colors"
                            >
                              <span className="text-[12px] font-semibold text-[#1D1D22] font-poppins">
                                Calendar Preview
                              </span>
                              <span className="text-[11px] text-blue-600 font-poppins">
                                {showCalendarPreview ? "Hide" : "View"}
                              </span>
                            </button>

                            {showCalendarPreview && (
                              <div className="mt-2">
                                {!effectiveManualStartDate ? (
                                  <p className="text-[11px] text-gray-500 font-poppins">
                                    Select start date to preview session days.
                                  </p>
                                ) : isTwiceOrThriceFrequency &&
                                  manualSelectedWeekDays.length !==
                                    requiredWeekDayCount ? (
                                  <p className="text-[11px] text-gray-500 font-poppins">
                                    Select {requiredWeekDayCount} weekdays to
                                    calculate {effectiveSessionCount} sessions.
                                  </p>
                                ) : !effectiveManualEndDate ? (
                                  <p className="text-[11px] text-gray-500 font-poppins">
                                    Unable to calculate end date for selected
                                    session count.
                                  </p>
                                ) : calendarPreviewMonths.length === 0 ? (
                                  <p className="text-[11px] text-gray-500 font-poppins">
                                    Calendar preview is not available for
                                    selected dates.
                                  </p>
                                ) : (
                                  <div className="space-y-1.5">
                                    <div className="border border-[#E9EDF7] bg-[#FDFEFF] rounded-[10px] p-2">
                                      <div className="flex items-center justify-between mb-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (!canGoPrevCalendarMonth) return;
                                            const prevMonth =
                                              calendarPreviewMonths[
                                                activeCalendarMonthIndex - 1
                                              ];
                                            if (prevMonth) {
                                              setCalendarPreviewMonthKey(
                                                prevMonth.key,
                                              );
                                            }
                                          }}
                                          disabled={!canGoPrevCalendarMonth}
                                          className="h-6 w-6 rounded-md border border-[#E4E9F5] text-[#6F7B92] disabled:opacity-35 disabled:cursor-not-allowed hover:bg-[#F3F6FF] flex items-center justify-center transition-colors"
                                        >
                                          <span className="text-[12px]">
                                            &lsaquo;
                                          </span>
                                        </button>

                                        <p className="text-[11px] font-semibold text-[#495267] font-poppins">
                                          {activeCalendarMonth?.label || "-"}
                                        </p>

                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (!canGoNextCalendarMonth) return;
                                            const nextMonth =
                                              calendarPreviewMonths[
                                                activeCalendarMonthIndex + 1
                                              ];
                                            if (nextMonth) {
                                              setCalendarPreviewMonthKey(
                                                nextMonth.key,
                                              );
                                            }
                                          }}
                                          disabled={!canGoNextCalendarMonth}
                                          className="h-6 w-6 rounded-md border border-[#E4E9F5] text-[#6F7B92] disabled:opacity-35 disabled:cursor-not-allowed hover:bg-[#F3F6FF] flex items-center justify-center transition-colors"
                                        >
                                          <span className="text-[12px]">
                                            &rsaquo;
                                          </span>
                                        </button>
                                      </div>

                                      <div className="grid grid-cols-7 gap-1 mb-1">
                                        {CALENDAR_WEEK_HEADERS.map((head) => (
                                          <div
                                            key={`calendar-head-${head}`}
                                            className="text-[9px] text-[#9AA4B7] text-center font-poppins"
                                          >
                                            {head}
                                          </div>
                                        ))}
                                      </div>

                                      <div className="grid grid-cols-7 gap-1">
                                        {activeCalendarMonth?.cells.map(
                                          (cell) => {
                                            const isScheduled =
                                              previewSessionDaySet.has(
                                                cell.dateKey,
                                              );

                                            return (
                                              <div
                                                key={`${activeCalendarMonth?.key}-month-cell-${cell.dateKey}`}
                                                className={`h-6 rounded-[6px] text-[10px] font-poppins flex items-center justify-center border transition-colors ${
                                                  cell.inCurrentMonth
                                                    ? "border-[#EDF1FA] text-[#586074] bg-white"
                                                    : "border-transparent text-[#C6CCDA] bg-transparent"
                                                } ${
                                                  isScheduled
                                                    ? "bg-[#4361EE] text-white border-2 border-[#2F4CD6] font-semibold shadow-[0_0_0_1px_rgba(47,76,214,0.18)]"
                                                    : ""
                                                }`}
                                              >
                                                {cell.label}
                                              </div>
                                            );
                                          },
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    {/* Remaining assignment panels remain same layout, but use showStaticAssignment
                        instead of isRequestedStatus to prevent dropdowns in readOnly. */}

                    {selectedPatient &&
                      (!selectedPatient.dutyInfo ||
                        selectedPatient.dutyInfo.length === 0) &&
                      isPhysiotherapist && (
                        <div className="mt-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {!isPhysioNoAssessment && (
                              <div className="border border-gray-200 rounded-[12px] p-2">
                                <div className="mb-2 flex items-center justify-between gap-2">
                                  <p className="text-[13px] font-semibold text-[#1D1D22] font-poppins whitespace-nowrap">
                                    {dutyLabel}
                                  </p>
                                  {renderAvailabilityButton()}
                                </div>

                                {showStaticAssignment ? (
                                  <div className="flex items-center gap-2 px-2 py-1.5">
                                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                      <img
                                        src={CommonImages.nurseNew}
                                        alt="Avatar"
                                        className="w-6 h-6 object-cover rounded-full"
                                      />
                                    </div>
                                    <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[150px]">
                                      {getAssignedHCPName(selectedPatient)}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="relative flex-1">
                                    <select
                                      value={selectedMainStaff || ""}
                                      onChange={(e) => {
                                        handleSingleStaffChange(e.target.value);
                                      }}
                                      onFocus={async () => {
                                        if (assignStaffList.length === 0) {
                                          await getNames();
                                        }
                                      }}
                                      disabled={
                                        !isPendingStatus(selectedPatient)
                                      }
                                      className={`w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 ${
                                        !isPendingStatus(selectedPatient)
                                          ? "opacity-50 cursor-not-allowed"
                                          : ""
                                      }`}
                                    >
                                      <option value="" hidden>
                                        {isPhysioNoAssessment
                                          ? "Select Duty Staff"
                                          : "Select assigned HCP"}
                                      </option>

                                      {assignStaffList
                                        .filter(
                                          (staff) =>
                                            staff._id !== selectedStandby1 &&
                                            (!isPhysioNoAssessment ||
                                              staff._id !== selectedStandby2),
                                        )
                                        .map((staff) => (
                                          <option
                                            key={staff._id}
                                            value={staff._id}
                                          >
                                            {getStaffDisplayName(staff)}
                                          </option>
                                        ))}
                                    </select>

                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                      <svg
                                        className="fill-current h-3 w-3"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                      >
                                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                      </svg>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="border border-gray-200 rounded-[12px] p-2">
                              <p className="text-[13px] font-semibold text-[#1D1D22] font-poppins mb-2 whitespace-nowrap">
                                {physioStandbyLabel}
                              </p>

                              {showStaticAssignment ? (
                                <div className="flex items-center gap-2 px-2 py-1.5">
                                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                    <img
                                      src={CommonImages.nurseNew}
                                      alt="Avatar"
                                      className="w-6 h-6 object-cover rounded-full"
                                    />
                                  </div>
                                  <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[150px]">
                                    {getStandbyHCPName(selectedPatient)}
                                  </span>
                                </div>
                              ) : (
                                <div className="relative flex-1">
                                  <select
                                    value={selectedStandby1 || ""}
                                    onChange={(e) => {
                                      setSelectedStandby1(e.target.value);
                                      setIsEditingStaff(true);
                                    }}
                                    onFocus={async () => {
                                      if (assignStaffList.length === 0) {
                                        await getNames();
                                      }
                                    }}
                                    disabled={!isPendingStatus(selectedPatient)}
                                    className={`w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 ${
                                      !isPendingStatus(selectedPatient)
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                  >
                                    <option value="" hidden>
                                      {isPhysioNoAssessment
                                        ? "Select Standby Staff 1"
                                        : "Select standby staff"}
                                    </option>

                                    {assignStaffList
                                      .filter(
                                        (staff) =>
                                          staff._id !== selectedMainStaff &&
                                          staff._id !== assessmentStaff &&
                                          (!isPhysioNoAssessment ||
                                            staff._id !== selectedStandby2),
                                      )
                                      .map((staff) => (
                                        <option
                                          key={staff._id}
                                          value={staff._id}
                                        >
                                          {getStaffDisplayName(staff)}
                                        </option>
                                      ))}
                                  </select>

                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                    <svg
                                      className="fill-current h-3 w-3"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </div>

                            {isPhysioNoAssessment && (
                              <div className="border border-gray-200 rounded-[12px] p-2">
                                <p className="text-[13px] font-semibold text-[#1D1D22] font-poppins mb-2 whitespace-nowrap">
                                  {standby2Label}
                                </p>

                                {showStaticAssignment ? (
                                  <div className="flex items-center gap-2 px-2 py-1.5">
                                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                      <img
                                        src={CommonImages.nurseNew}
                                        alt="Avatar"
                                        className="w-6 h-6 object-cover rounded-full"
                                      />
                                    </div>
                                    <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[150px]">
                                      {selectedPatient?.backupSpecialist
                                        ?.name || "N/A"}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="relative flex-1">
                                    <select
                                      value={selectedStandby2 || ""}
                                      onChange={(e) => {
                                        setSelectedStandby2(e.target.value);
                                        setIsEditingStaff(true);
                                      }}
                                      onFocus={async () => {
                                        if (assignStaffList.length === 0) {
                                          await getNames();
                                        }
                                      }}
                                      disabled={
                                        !isPendingStatus(selectedPatient)
                                      }
                                      className={`w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 ${
                                        !isPendingStatus(selectedPatient)
                                          ? "opacity-50 cursor-not-allowed"
                                          : ""
                                      }`}
                                    >
                                      <option value="" hidden>
                                        Select Standby Staff 2 (Optional)
                                      </option>

                                      {assignStaffList
                                        .filter(
                                          (staff) =>
                                            staff._id !== selectedMainStaff &&
                                            staff._id !== selectedStandby1 &&
                                            staff._id !== assessmentStaff,
                                        )
                                        .map((staff) => (
                                          <option
                                            key={staff._id}
                                            value={staff._id}
                                          >
                                            {getStaffDisplayName(staff)}
                                          </option>
                                        ))}
                                    </select>

                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                      <svg
                                        className="fill-current h-3 w-3"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                      >
                                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                      </svg>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    {selectedPatient &&
                      (!selectedPatient.dutyInfo ||
                        selectedPatient.dutyInfo.length === 0) &&
                      !isPhysiotherapist && (
                        <div className="mt-4">
                          <h4 className="text-[14px] font-semibold text-gray-800 mb-3 whitespace-nowrap">
                            Standby Staff
                          </h4>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="border border-gray-200 rounded-[12px] p-2">
                              <p className="text-[13px] font-semibold text-[#1D1D22] font-poppins mb-2 whitespace-nowrap">
                                {standby1Label}
                              </p>

                              {showStaticAssignment ? (
                                <div className="flex items-center gap-2 px-2 py-1.5">
                                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                    <img
                                      src={CommonImages.nurseNew}
                                      alt="Avatar"
                                      className="w-6 h-6 object-cover rounded-full"
                                    />
                                  </div>
                                  <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[150px]">
                                    {getStandbyHCPName(selectedPatient)}
                                  </span>
                                </div>
                              ) : (
                                <div className="relative flex-1">
                                  <select
                                    value={selectedStandby1 || ""}
                                    onChange={(e) => {
                                      setSelectedStandby1(e.target.value);
                                      setIsEditingStaff(true);
                                    }}
                                    onFocus={async () => {
                                      if (assignStaffList.length === 0) {
                                        await getNames();
                                      }
                                    }}
                                    disabled={!isPendingStatus(selectedPatient)}
                                    className={`w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 ${
                                      !isPendingStatus(selectedPatient)
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                  >
                                    <option value="" hidden>
                                      Select standby staff
                                    </option>

                                    {assignStaffList
                                      .filter(
                                        (staff) =>
                                          staff._id !== selectedMainStaff &&
                                          staff._id !== selectedStandby2,
                                      )
                                      .map((staff) => (
                                        <option
                                          key={staff._id}
                                          value={staff._id}
                                        >
                                          {getStaffDisplayName(staff)}
                                        </option>
                                      ))}
                                  </select>

                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                    <svg
                                      className="fill-current h-3 w-3"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="border border-gray-200 rounded-[12px] p-2">
                              <p className="text-[13px] font-semibold text-[#1D1D22] font-poppins mb-2 whitespace-nowrap">
                                {standby2Label}
                              </p>

                              {showStaticAssignment ? (
                                <div className="flex items-center gap-2 px-2 py-1.5">
                                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                    <img
                                      src={CommonImages.nurseNew}
                                      alt="Avatar"
                                      className="w-6 h-6 object-cover rounded-full"
                                    />
                                  </div>
                                  <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[150px]">
                                    {selectedPatient?.backupSpecialist?.name ||
                                      "N/A"}
                                  </span>
                                </div>
                              ) : (
                                <div className="relative flex-1">
                                  <select
                                    value={selectedStandby2 || ""}
                                    onChange={(e) => {
                                      setSelectedStandby2(e.target.value);
                                      setIsEditingStaff(true);
                                    }}
                                    onFocus={async () => {
                                      if (assignStaffList.length === 0) {
                                        await getNames();
                                      }
                                    }}
                                    disabled={!isPendingStatus(selectedPatient)}
                                    className={`w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 ${
                                      !isPendingStatus(selectedPatient)
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                  >
                                    <option value="" hidden>
                                      Select standby staff
                                    </option>

                                    {assignStaffList
                                      .filter(
                                        (staff) =>
                                          staff._id !== selectedMainStaff &&
                                          staff._id !== selectedStandby1,
                                      )
                                      .map((staff) => (
                                        <option
                                          key={staff._id}
                                          value={staff._id}
                                        >
                                          {getStaffDisplayName(staff)}
                                        </option>
                                      ))}
                                  </select>

                                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                    <svg
                                      className="fill-current h-3 w-3"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                    {(isNurseRegularPartnerPricingPending ||
                      isPhysioPartnerPricingPending ||
                      isNurseOneTimePartnerPricingPending) && (
                      <div className="mt-4 rounded-[12px] border border-[#F2D6A2] bg-[#FFF7E8] p-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 h-6 w-6 rounded-full bg-[#FBE8C6] text-[#B7791F] flex items-center justify-center text-[14px] font-semibold">
                            !
                          </div>
                          <div>
                            <p className="text-[14px] font-semibold text-[#8A5A12] font-poppins">
                              Pricing Request Pending
                            </p>
                            <p className="mt-1 text-[13px] text-[#8A5A12] font-poppins">
                              {pricingRequestPendingMessage}
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={handleConfigurePricing}
                                className="px-4 py-2 text-[12px] font-poppins font-semibold rounded-md bg-[#B7791F] text-white hover:bg-[#9E6618] transition-colors"
                              >
                                Configure Pricing
                              </button>
                              <button
                                type="button"
                                onClick={handleRejectPricingRequest}
                                className="px-4 py-2 text-[12px] font-poppins font-semibold rounded-md border border-[#E59D8A] text-[#C4573F] bg-white hover:bg-[#FFF1EE] transition-colors"
                              >
                                Reject Request
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!readOnly &&
                      selectedPatient &&
                      selectedPatient?.bookedBy === "partner" &&
                      (!selectedPatient.dutyInfo ||
                        selectedPatient.dutyInfo.length === 0) &&
                      isPendingStatus(selectedPatient) &&
                      !isNurseRegularPartnerPricingPending && (
                        <div className="mt-4 rounded-[12px] border border-gray-200 p-4">
                          <div>
                            <p className="text-[14px] font-semibold text-[#1D1D22] font-poppins">
                              Payment Mode
                            </p>
                            <p className="text-[12px] text-gray-500 font-poppins mt-1">
                              Choose whether this visit should be paid upfront
                              or paid daily.
                            </p>
                          </div>

                          <div className="mt-3 grid gap-2">
                            {[
                              {
                                value: "advance",
                                label: "Upfront Payment",
                                description: "Patient pays before duty starts.",
                              },
                              {
                                value: "daily",
                                label: "Daily Payment",
                                description:
                                  "Patient pays each day for that day's service.",
                              },
                            ].map((option) => {
                              const isSelected =
                                paymentModeCategory === option.value;

                              return (
                                <div key={option.value}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (option.value === "daily") {
                                        setPaymentModeCategory("daily");
                                        setPaymentMode("daily_qr");
                                      } else {
                                        setPaymentModeCategory("advance");
                                        setPaymentMode(advancePaymentMode);
                                      }
                                      setIsEditingStaff(true);
                                    }}
                                    className={`w-full rounded-[10px] border px-3 py-3 text-left transition-colors ${
                                      isSelected
                                        ? "border-blue-600 bg-blue-50"
                                        : "border-gray-200 bg-white hover:border-blue-300"
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div>
                                        <p className="text-[13px] font-semibold text-[#1D1D22] font-poppins">
                                          {option.label}
                                        </p>
                                        <p className="mt-1 text-[12px] text-gray-500 font-poppins">
                                          {option.description}
                                        </p>
                                      </div>
                                      <span
                                        className={`mt-0.5 h-4 w-4 rounded-full border ${
                                          isSelected
                                            ? "border-blue-600 bg-blue-600"
                                            : "border-gray-300 bg-white"
                                        }`}
                                      />
                                    </div>
                                  </button>

                                  {option.value === "advance" && isSelected && (
                                    <div className="mt-3 ml-4 border-l-2 border-blue-100 pl-4">
                                      <p className="text-[11px] text-gray-500 font-poppins">
                                        Choose how the upfront payment should be
                                        collected.
                                      </p>
                                      <div className="mt-3 grid gap-2">
                                        {ADVANCE_PAYMENT_MODE_OPTIONS.map(
                                          (advanceOption) => {
                                            const isAdvanceSelected =
                                              paymentMode ===
                                              advanceOption.value;

                                            return (
                                              <button
                                                key={advanceOption.value}
                                                type="button"
                                                onClick={() => {
                                                  setAdvancePaymentMode(
                                                    advanceOption.value,
                                                  );
                                                  setPaymentMode(
                                                    advanceOption.value,
                                                  );
                                                  setPaymentModeCategory(
                                                    "advance",
                                                  );
                                                  setIsEditingStaff(true);
                                                }}
                                                className={`w-full rounded-[10px] border px-3 py-2.5 text-left transition-colors ${
                                                  isAdvanceSelected
                                                    ? "border-blue-500 bg-blue-50 shadow-sm"
                                                    : "border-gray-200 bg-white hover:border-blue-200"
                                                }`}
                                              >
                                                <div className="flex items-start justify-between gap-3">
                                                  <div>
                                                    <p className="text-[12px] font-semibold text-[#1D1D22] font-poppins">
                                                      {advanceOption.label}
                                                    </p>
                                                    <p className="mt-1 text-[11px] text-gray-500 font-poppins">
                                                      {
                                                        advanceOption.description
                                                      }
                                                    </p>
                                                  </div>
                                                  <span
                                                    className={`mt-0.5 h-3.5 w-3.5 rounded-full border ${
                                                      isAdvanceSelected
                                                        ? "border-blue-500 bg-blue-500"
                                                        : "border-gray-300 bg-white"
                                                    }`}
                                                  />
                                                </div>
                                              </button>
                                            );
                                          },
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                  </div>

                  {/* ✅ Assign/Cancel buttons hidden in readOnly already via !readOnly */}
                  {!readOnly &&
                    selectedPatient &&
                    (!selectedPatient.dutyInfo ||
                      selectedPatient.dutyInfo.length === 0) &&
                    isPendingStatus(selectedPatient) &&
                    !isNurseRegularPartnerPricingPending &&
                    isEditingStaff && (
                      <div className="pt-2 mt-2 border-t flex justify-end gap-3">
                        <button
                          onClick={handleCancelStaffAssignment}
                          className="px-4 py-2 text-[12px] font-poppins bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>

                        <button
                          onClick={handleSaveStaffAssignment}
                          disabled={
                            loading ||
                            !selectedMainStaff ||
                            !selectedStandby1 ||
                            selectedMainStaff === selectedStandby1 ||
                            !paymentMode ||
                            !isManualVisitInfoValid
                          }
                          className="px-4 py-2 text-[12px] font-poppins bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                          {loading && (
                            <svg
                              className="animate-spin h-3.5 w-3.5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          )}
                          {loading ? "Assigning..." : "Assign"}
                        </button>
                      </div>
                    )}
                </>
              )}
            </>
          </div>
        </div>

        <div className="w-full min-[1100px]:w-[340px] lg:w-[380px] flex-shrink-0 min-w-0 overflow-hidden min-[1100px]:h-full h-auto">
          <PatientHistory
            selectedPatient={selectedPatient}
            dischargeSummaries={dischargeSummariesForHistory}
          />
        </div>
      </div>

      {timeModalOpen && dutyForModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl p-4">
            <h3 className="text-[16px] font-semibold font-poppins text-gray-800 mb-3">
              Change Time Slot
            </h3>

            <p className="text-[12px] text-gray-500 font-poppins mb-3">
              {moment(dutyForModal.date).format("DD/MM/YYYY - dddd")}
            </p>

            <select
              value={timeModalSelectedSlot}
              onChange={(e) => setTimeModalSelectedSlot(e.target.value)}
              className="w-full bg-[#F6F8FC] rounded-md px-3 py-2 text-[12px] font-poppins border border-gray-300 outline-none"
            >
              {PHYSIO_TIME_SLOTS.map((slot) => (
                <option key={slot} value={slot}>
                  {slot}
                </option>
              ))}
            </select>

            <div className="pt-4 mt-4 border-t flex justify-end gap-3">
              <button
                onClick={closeTimeModal}
                className="px-4 py-2 text-[12px] font-poppins bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={applySelectedTimeSlot}
                className="px-4 py-2 text-[12px] font-poppins bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {availabilityModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
          onClick={closeAvailabilityModal}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="staff-availability-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4">
              <div>
                <div className="flex items-center gap-2">
                  <CalendarCheck2
                    className="h-5 w-5 text-[#4361EE]"
                    aria-hidden="true"
                  />
                  <h3
                    id="staff-availability-title"
                    className="text-[16px] font-bold text-slate-800 font-poppins"
                  >
                    Duty Staff Availability
                  </h3>
                </div>
                <p className="mt-1 text-[11px] text-slate-500 font-poppins">
                  {availabilityContext?.slot
                    ? "Free means available for every selected duty or session."
                    : "Free means no occupied duty on the selected date."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeAvailabilityModal}
                autoFocus
                className="rounded-full p-1.5 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
                aria-label="Close staff availability"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            {availabilityContext && (
              <div className="grid grid-cols-1 gap-2 border-b border-slate-200 bg-white px-5 py-3 text-[11px] sm:grid-cols-2 font-poppins">
                <div>
                  <span className="font-semibold text-slate-500">Role:</span>{" "}
                  <span className="text-slate-800">
                    {formatAvailabilityRole(availabilityContext.role)}
                  </span>
                </div>
                <div>
                  <span className="font-semibold text-slate-500">Subrole:</span>{" "}
                  <span className="text-slate-800">
                    {formatAvailabilityRole(availabilityContext.subRole)}
                  </span>
                </div>
                <div>
                  <span className="font-semibold text-slate-500">Dates:</span>{" "}
                  <span className="text-slate-800">
                    {availabilityDateLabel}
                  </span>
                </div>
                <div>
                  <span className="font-semibold text-slate-500">Slot:</span>{" "}
                  <span className="text-slate-800">
                    {availabilityContext.slot || "All times (date view)"}
                  </span>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-5">
              {availabilityLoading ? (
                <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 text-slate-500">
                  <div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-200 border-t-[#4361EE]" />
                  <p className="text-[12px] font-medium font-poppins">
                    {availabilityContext?.slot
                      ? "Checking the complete schedule..."
                      : "Checking the selected date..."}
                  </p>
                </div>
              ) : availabilityError ? (
                <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 text-center">
                  <p className="max-w-md text-[12px] text-red-600 font-poppins">
                    {availabilityError}
                  </p>
                  <button
                    type="button"
                    onClick={() => getNames(true)}
                    className="rounded-lg bg-[#4361EE] px-4 py-2 text-[11px] font-semibold text-white transition-colors hover:bg-[#354FCF] font-poppins"
                  >
                    Retry
                  </button>
                </div>
              ) : availabilityStaffList.length === 0 ? (
                <div className="flex min-h-[220px] items-center justify-center text-center">
                  <p className="text-[12px] text-slate-500 font-poppins">
                    No eligible specialists were found for this role and
                    subrole.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <section className="overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50/40">
                    <div className="flex items-center justify-between border-b border-emerald-200 bg-emerald-50 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2
                          className="h-4 w-4 text-emerald-600"
                          aria-hidden="true"
                        />
                        <h4 className="text-[12px] font-bold text-emerald-800 font-poppins">
                          Free
                        </h4>
                      </div>
                      <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white font-poppins">
                        {availabilityGroups.free.length}
                      </span>
                    </div>
                    <div className="max-h-[330px] divide-y divide-emerald-100 overflow-y-auto">
                      {availabilityGroups.free.length > 0 ? (
                        availabilityGroups.free.map((specialist) => (
                          <div
                            key={specialist._id}
                            className="px-4 py-3 text-[12px] font-medium text-slate-800 font-poppins"
                          >
                            {getStaffDisplayName(specialist) ||
                              "Unnamed specialist"}
                          </div>
                        ))
                      ) : (
                        <p className="px-4 py-5 text-center text-[11px] text-slate-500 font-poppins">
                          No specialist is free for the complete schedule.
                        </p>
                      )}
                    </div>
                  </section>

                  <section className="overflow-hidden rounded-xl border border-amber-200 bg-amber-50/40">
                    <div className="flex items-center justify-between border-b border-amber-200 bg-amber-50 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Clock3
                          className="h-4 w-4 text-amber-600"
                          aria-hidden="true"
                        />
                        <h4 className="text-[12px] font-bold text-amber-800 font-poppins">
                          Occupied
                        </h4>
                      </div>
                      <span className="rounded-full bg-amber-600 px-2 py-0.5 text-[10px] font-bold text-white font-poppins">
                        {availabilityGroups.occupied.length}
                      </span>
                    </div>
                    <div className="max-h-[330px] divide-y divide-amber-100 overflow-y-auto">
                      {availabilityGroups.occupied.length > 0 ? (
                        availabilityGroups.occupied.map((specialist) => (
                          <div key={specialist._id} className="px-4 py-3">
                            <p className="text-[12px] font-semibold text-slate-800 font-poppins">
                              {getStaffDisplayName(specialist) ||
                                "Unnamed specialist"}
                            </p>
                            <div className="mt-1 space-y-1">
                              {Array.isArray(
                                specialist?.availability?.conflicts,
                              ) &&
                                specialist.availability.conflicts.map(
                                  (conflict, index) => (
                                    <p
                                      key={`${specialist._id}-conflict-${index}`}
                                      className="text-[10px] text-amber-700 font-poppins"
                                    >
                                      {formatAvailabilityDate(conflict.date)}
                                      {" | "}
                                      {formatAvailabilityTime(
                                        conflict.startTime,
                                      )}
                                      {conflict.endTime
                                        ? ` - ${formatAvailabilityTime(
                                            conflict.endTime,
                                          )}`
                                        : ""}
                                    </p>
                                  ),
                                )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="px-4 py-5 text-center text-[11px] text-slate-500 font-poppins">
                          No occupied specialists.
                        </p>
                      )}
                    </div>
                  </section>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showTracking && trackingData ? (
        <LiveTrackingMap
          open={showTracking}
          onClose={() => setShowTracking(false)}
          trackingData={trackingData}
        />
      ) : null}

      {/* TIMELINE MODAL */}
      {timelineModalOpen && timelineVisit && readOnly && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl h-[70vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div>
                <h3 className="text-[16px] font-bold text-gray-800 font-poppins">
                  Visit Timeline Report
                </h3>
                <p className="text-[12px] text-slate-500 font-semibold mt-0.5 font-poppins">
                  Patient: {timelineVisit?.patient?.name || "N/A"}
                </p>
              </div>
              <button
                onClick={closeTimelineModal}
                className="p-1.5 rounded-full hover:bg-slate-200 transition-colors"
              >
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-6">
              {Array.isArray(timelineVisit?.timeline) &&
              timelineVisit.timeline.length > 0 ? (
                <div className="relative border-l border-blue-200 ml-4 pl-6 py-2">
                  {timelineVisit.timeline.reduce((acc, item, idx) => {
                    const dateStr = item?.dateTime
                      ? moment(item.dateTime).format("DD/MM/YYYY")
                      : "-";
                    const prevDateStr =
                      idx > 0
                        ? timelineVisit.timeline[idx - 1]?.dateTime
                          ? moment(
                              timelineVisit.timeline[idx - 1].dateTime,
                            ).format("DD/MM/YYYY")
                          : "-"
                        : null;

                    if (dateStr !== prevDateStr) {
                      acc.push(
                        <div
                          key={`date-${dateStr}-${idx}`}
                          className="relative h-8 mb-6 mt-2"
                        >
                          <div className="absolute -left-[24px] top-0 transform -translate-x-1/2 ml-6 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-[11px] font-bold font-poppins border border-blue-200 whitespace-nowrap z-10 shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
                            {dateStr}
                          </div>
                        </div>,
                      );
                    }

                    acc.push(
                      <div
                        key={item?._id || idx}
                        className="relative mb-8 last:mb-2"
                      >
                        {/* Dot on Timeline line */}
                        <div className="absolute -left-[32px] top-[4px]">
                          <div className="h-4 w-4 rounded-full bg-white border-[4px] border-blue-600 shadow-sm" />
                        </div>

                        <div>
                          <div className="text-[11px] font-semibold text-slate-400 font-poppins">
                            {item?.dateTime
                              ? moment(item.dateTime).format("hh:mm A")
                              : "-"}
                          </div>
                          <div className="text-[14px] font-bold text-gray-800 mt-0.5 font-poppins">
                            {item?.event || "—"}
                          </div>
                          <p className="text-[12px] text-gray-600 mt-1 font-poppins whitespace-pre-line leading-relaxed">
                            {item?.description || "—"}
                          </p>
                          {(item?.actor?.name || item?.actor?.role) && (
                            <div className="mt-1.5 text-[10px] font-semibold text-[#3E57E8] font-poppins">
                              By:{" "}
                              {item.actor.name
                                ? `${item.actor.name} (${capitalizeFirstLetter(
                                    item.actor.role || "",
                                  )})`
                                : capitalizeFirstLetter(item.actor.role || "")}
                            </div>
                          )}
                        </div>
                      </div>,
                    );

                    return acc;
                  }, [])}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500 text-xs font-semibold font-poppins">
                  No timeline events recorded.
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-slate-50 flex justify-end">
              <button
                onClick={closeTimelineModal}
                className="px-5 py-2 rounded-xl bg-blue-600 text-white text-[12px] font-semibold font-poppins hover:bg-blue-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientList;
